import {
  type CSSProperties,
  type KeyboardEvent,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { deepEqual } from 'fast-equals';
import { MoreVertical, RotateCcw, Save, Trash2, X } from 'lucide-react';
import { UserContext } from '../../../context/authContext';
import { DocumentContext } from '../../../context/documentContext';
import { usePolicyContext } from '../../../context/policyContext';
import { useAlert } from '../../../context/alertContext';
import { useUnsavedChangesWarning } from '../rfpUploader/useUnsavedChangesWarning';
import { ManualBenchmarkRequestBody } from '../../../lib/models/interfaces';
import { primaryButtonGreen } from '../../tokenStyle/tokeReset';
import {
  PdfViewerAnnotationRequest,
  PdfViewerAnnotationReference,
  PdfViewerAnnotationSelection,
  PdfViewerAnnotationStyle,
  PdfViewerPreparedAnnotationBatch,
  PdfViewerPreparedAnnotationBatchMap,
  PdfViewerTextSelectionMatch,
  SearchablePdfViewerHandle
} from './searchablePdfViewer';
import { PdfFallbackPageItemsByPage } from './search/pdfFallbackSearch';
import { groupPdfFallbackTextItemsByPage } from './search/pdfFallbackSearch-old';
import {
  Alert,
  Box,
  Button,
  classNames,
  Container,
  FormField,
  Header,
  Input,
  Select,
  type SelectOptionDefinition as OptionDefinition,
  SpaceBetween,
  Spinner
} from './viewerSharedUi';

interface AnalyzedContractViewerProps {
  clickedHighlightSentenceIds?: string[];
  clickedHighlightSelectionKey?: number;
  currentPdfPage?: number | null;
  manualBenchmarkSelection?:
    | (PdfViewerTextSelectionMatch & {
        selectionKey: number;
      })
    | null;
  onSelectSentenceIds?: (sentenceIds: string[]) => void;
  onDebugHighlightSelection?: (selection: {
    candidateId: string;
    sentenceIds: string[];
  }) => void;
  onPreserveHighlightsOnPageChangeChange?: (
    preserveHighlightsOnPageChange: boolean
  ) => void;
  onLoadingStateChange?: (isLoading: boolean) => void;
  showPdf?: boolean;
  onToggleShowPdf?: () => void;
  reducedWidthMode?: boolean;
  selectedAnnotationReference?: PdfViewerAnnotationSelection | null;
  viewerRef: React.RefObject<SearchablePdfViewerHandle | null>;
  fallbackPageItems: PdfFallbackPageItemsByPage;
  setFallbackPageItems: (value: PdfFallbackPageItemsByPage) => void;
}

type PolicyValue =
  | string
  | number
  | boolean
  | null
  | PolicyValue[]
  | { [key: string]: PolicyValue };

interface PolicyAppliesToNormalized {
  text: string[];
  node_refs: string[];
}

interface PolicyNormalizationCaution {
  level: string;
  has_soft_or_hard_to_compare_wording: boolean;
  notes: string[];
}

interface PolicyNormalizedRule {
  normalized_dimension: string;
  primary_comparison_mechanic: string;
  comparison_type: string;
  expected_value_structure_type: string;
  expected_value_structure: Record<string, PolicyValue>;
  normalized_rule_statement: string;
  normalized_rule_statement_template: string;
  source: {
    source_text: string;
    contextIds: string[];
    pageNumbers: number[];
    sentenceIds: string[];
  };
  applies_to_normalized: PolicyAppliesToNormalized;
  normalization_caution: PolicyNormalizationCaution;
}

interface PolicyCandidateItem {
  candidate_id: string;
  normalized_rule: PolicyNormalizedRule;
  embedding: number[];
  embeddingSourceText: number[];
  isUserCreated?: boolean;
  edited?: boolean;
  editedByUserId?: string;
  editedByName?: string;
}

interface PolicyDetailsState {
  policyName: string;
  version: string;
  type: string;
}

interface PolicyEditorState {
  policyDetails: PolicyDetailsState;
  policyCategories: PolicyCandidateItem[];
  policyViewerProcessed: boolean;
}

interface GroupedPolicyCandidate {
  index: number;
  item: PolicyCandidateItem;
}

interface PolicyCandidateGroup {
  dimensionKey: string;
  dimensionLabel: string;
  items: GroupedPolicyCandidate[];
}

interface ManualBenchmarkDraft {
  pageNumbers: number[];
  sourceText: string;
  selectionKey: number;
}

type SavedPreparedPageAnnotationBatches = Record<
  string,
  PdfViewerPreparedAnnotationBatch
>;

const EMPTY_POLICY_DETAILS: PolicyDetailsState = {
  policyName: '',
  version: '',
  type: ''
};

const MANUAL_BENCHMARK_CARD_KEY = '__manual-benchmark-draft__';
const UNCATEGORIZED_DIMENSION_KEY = '__uncategorized__';
const HIGHLIGHT_ALL_BATCH_CACHE_KEY = 'highlight-all';
const POLICY_VIEWER_PROCESSED_FLAG = 'policyViewerProcessed';
const USER_CREATED_BENCHMARK_FILTER_VALUE = 'user-created';
const getHighlightAllPageCacheKey = (pageNumber: number) =>
  `${HIGHLIGHT_ALL_BATCH_CACHE_KEY}::page-${pageNumber}`;
const getPageNumberFromHighlightAllCacheKey = (cacheKey: string) => {
  const prefix = `${HIGHLIGHT_ALL_BATCH_CACHE_KEY}::page-`;

  if (!cacheKey.startsWith(prefix)) {
    return null;
  }

  const parsedPageNumber = Number(cacheKey.slice(prefix.length));

  return Number.isInteger(parsedPageNumber) && parsedPageNumber > 0
    ? parsedPageNumber
    : null;
};

const getPolicyCandidateCardKey = (
  item: PolicyCandidateItem,
  index: number
) => {
  const normalizedCandidateId = item.candidate_id.trim();

  return normalizedCandidateId || `policy-candidate-${index}`;
};

const isUserCreatedPolicyCandidate = (item: PolicyCandidateItem) =>
  item.isUserCreated === true;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const cloneValue = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

const normalizePreparedAnnotationBatch = (
  pageNumber: number,
  value: unknown
): PdfViewerPreparedAnnotationBatch | null => {
  if (!isRecord(value) || !Array.isArray(value.descriptors)) {
    return null;
  }

  return {
    activePageNumber:
      typeof value.activePageNumber === 'number' &&
      Number.isInteger(value.activePageNumber) &&
      value.activePageNumber > 0
        ? value.activePageNumber
        : null,
    cacheKey: getHighlightAllPageCacheKey(pageNumber),
    descriptors: cloneValue(value.descriptors),
    query: typeof value.query === 'string' ? value.query : '',
    requestSignature:
      typeof value.requestSignature === 'string' ? value.requestSignature : '',
    skippedAnnotations: toFiniteNumber(value.skippedAnnotations) ?? 0,
    totalResults: toFiniteNumber(value.totalResults) ?? 0
  };
};

const normalizeSavedPreparedPageAnnotationBatches = (value: unknown) => {
  if (!isRecord(value)) {
    return {} satisfies SavedPreparedPageAnnotationBatches;
  }

  return Object.fromEntries(
    Object.entries(value).flatMap(([pageKey, batchValue]) => {
      const pageNumber = toFiniteNumber(pageKey);

      if (!pageNumber || !Number.isInteger(pageNumber) || pageNumber < 1) {
        return [];
      }

      const normalizedBatch = normalizePreparedAnnotationBatch(
        pageNumber,
        batchValue
      );

      return normalizedBatch ? [[String(pageNumber), normalizedBatch]] : [];
    })
  ) as SavedPreparedPageAnnotationBatches;
};

const buildPreparedAnnotationBatchCacheMap = (
  batches: SavedPreparedPageAnnotationBatches
): PdfViewerPreparedAnnotationBatchMap =>
  Object.fromEntries(
    Object.entries(batches).map(([pageKey, batch]) => {
      const pageNumber = Number(pageKey);
      const cacheKey = getHighlightAllPageCacheKey(pageNumber);

      return [
        cacheKey,
        cloneValue({
          ...batch,
          cacheKey
        })
      ];
    })
  );

const filterSavedPreparedPageAnnotationBatches = (
  batches: SavedPreparedPageAnnotationBatches,
  batchSignatureByPage: Map<number, string>
) =>
  Object.fromEntries(
    Object.entries(batches).flatMap(([pageKey, batch]) => {
      const pageNumber = Number(pageKey);
      const expectedSignature = batchSignatureByPage.get(pageNumber);

      if (!expectedSignature || batch.requestSignature !== expectedSignature) {
        return [];
      }

      return [
        [
          pageKey,
          cloneValue({
            ...batch,
            cacheKey: getHighlightAllPageCacheKey(pageNumber)
          })
        ]
      ];
    })
  ) as SavedPreparedPageAnnotationBatches;

const buildSavedPreparedPageAnnotationBatchesFromCacheMap = (
  batches: PdfViewerPreparedAnnotationBatchMap,
  batchSignatureByPage: Map<number, string>,
  pageNumbers?: number[]
) => {
  const allowedPageNumberSet =
    pageNumbers && pageNumbers.length > 0 ? new Set(pageNumbers) : null;

  return Object.fromEntries(
    Object.entries(batches).flatMap(([cacheKey, batch]) => {
      const pageNumber = getPageNumberFromHighlightAllCacheKey(cacheKey);

      if (!pageNumber) {
        return [];
      }

      if (allowedPageNumberSet && !allowedPageNumberSet.has(pageNumber)) {
        return [];
      }

      const expectedSignature = batchSignatureByPage.get(pageNumber);

      if (!expectedSignature || batch.requestSignature !== expectedSignature) {
        return [];
      }

      return [
        [
          String(pageNumber),
          cloneValue({
            ...batch,
            cacheKey: getHighlightAllPageCacheKey(pageNumber)
          })
        ]
      ];
    })
  ) as SavedPreparedPageAnnotationBatches;
};

const buildPreparedPageSignaturesFromSavedBatches = (
  batches: SavedPreparedPageAnnotationBatches,
  batchSignatureByPage: Map<number, string>
) =>
  Object.fromEntries(
    Object.entries(batches).flatMap(([pageKey, batch]) => {
      const pageNumber = Number(pageKey);
      const expectedSignature = batchSignatureByPage.get(pageNumber);

      if (!expectedSignature || batch.requestSignature !== expectedSignature) {
        return [];
      }

      return [[pageNumber, batch.requestSignature]];
    })
  ) as Record<number, string>;

const getPolicyNameFromFileName = (fileName: string | undefined) => {
  if (!fileName) {
    return '';
  }

  return fileName.replace(/\.[^/.]+$/, '');
};

const toFiniteNumber = (value: unknown): number | null => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const normalizePageNumbers = (values: Array<number | null | undefined>) =>
  Array.from(
    new Set(
      values.filter(
        (value): value is number =>
          typeof value === 'number' && Number.isInteger(value) && value > 0
      )
    )
  ).sort((leftPageNumber, rightPageNumber) => leftPageNumber - rightPageNumber);

const normalizeHighlightAllQueryText = (input: string) =>
  input
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(
      /['\u0060\u00B4\u02B9\u02BB\u02BC\u02BD\u02BE\u02BF\u0374\u0384\u055A\u05F3\u07F4\u07F5\u2018\u2019\u201A\u201B\u2032\u2035\u275B\u275C\uA78C\uFF07]/g,
      "'"
    )
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    .replace(/[\u2010\u2011\u2012\u2013\u2014\u2212]/g, '-')
    .replace(/[\u00A0\u2000-\u200B\u202F\u205F\u3000]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeHighlightAllQueries = (query: string | string[]) => {
  const sourceQueries = Array.isArray(query) ? query : [query];

  return [
    ...new Set(
      sourceQueries
        .map((value) => normalizeHighlightAllQueryText(value))
        .filter(Boolean)
    )
  ];
};

const toStringValue = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (value === null || value === undefined) {
    return '';
  }

  try {
    return JSON.stringify(value);
  } catch (_error) {
    return '';
  }
};

const toPolicyValue = (value: unknown): PolicyValue => {
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value === null
  ) {
    return value as string | number | boolean | null;
  }

  if (Array.isArray(value)) {
    return value.map((entry) => toPolicyValue(entry));
  }

  if (isRecord(value)) {
    return Object.entries(value).reduce<Record<string, PolicyValue>>(
      (accumulator, [key, entry]) => {
        accumulator[key] = toPolicyValue(entry);
        return accumulator;
      },
      {}
    );
  }

  if (value === undefined) {
    return '';
  }

  return toStringValue(value);
};

const toStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => toStringValue(entry).trim())
    .filter((entry) => entry.length > 0);
};

const normalizeContextIds = (values: Array<string | null | undefined>) =>
  Array.from(
    new Set(
      values
        .map((value) => (typeof value === 'string' ? value.trim() : ''))
        .filter(Boolean)
    )
  ).sort((leftContextId, rightContextId) =>
    leftContextId.localeCompare(rightContextId)
  );

const toContextIdArray = (...values: unknown[]): string[] =>
  normalizeContextIds(
    values.flatMap((value) =>
      Array.isArray(value)
        ? value.map((entry) => toStringValue(entry).trim())
        : [toStringValue(value).trim()]
    )
  );

const toPageNumberArray = (value: unknown): number[] => {
  if (Array.isArray(value)) {
    return normalizePageNumbers(value.map((entry) => toFiniteNumber(entry)));
  }

  return normalizePageNumbers([toFiniteNumber(value)]);
};

const toBooleanValue = (value: unknown): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }

  return Boolean(value);
};

const isVerticallyScrollable = (element: HTMLElement | null): boolean => {
  if (!element) {
    return false;
  }

  const { overflowY } = window.getComputedStyle(element);

  return (
    element.scrollHeight > element.clientHeight &&
    (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay')
  );
};

const getNearestScrollContainer = (
  element: HTMLElement | null
): HTMLElement | null => {
  let currentElement = element?.parentElement ?? null;

  while (currentElement) {
    if (isVerticallyScrollable(currentElement)) {
      return currentElement;
    }

    currentElement = currentElement.parentElement;
  }

  return null;
};

const formatFieldLabel = (value: string): string =>
  value
    .split(/[._]/)
    .filter((entry) => entry.length > 0)
    .map((entry) => entry.charAt(0).toUpperCase() + entry.slice(1))
    .join(' ');

const formatVariableLabel = (value: string): string =>
  value
    .replace(/_/g, ' ')
    .split('.')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0)
    .map((entry) =>
      /^\d+$/.test(entry)
        ? `Option ${Number(entry) + 1}`
        : entry.charAt(0).toUpperCase() + entry.slice(1)
    )
    .join(' ');

const getNormalizedDimensionLabel = (value: string): string => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return 'Uncategorized';
  }

  return formatFieldLabel(trimmedValue);
};

const getPolicyCandidateDimensionKey = (item: PolicyCandidateItem) => {
  const dimensionKey = item.normalized_rule.normalized_dimension.trim();

  return dimensionKey || UNCATEGORIZED_DIMENSION_KEY;
};

const formatPrimitiveValue = (value: PolicyValue): string => {
  if (typeof value === 'string') {
    return value.replace(/_/g, ' ');
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (value === null) {
    return '';
  }

  return toStringValue(value);
};

const normalizeEditableWhitespace = (value: string): string => {
  return value.replace(/\s+/g, ' ');
};

const coercePrimitiveValue = (
  nextValue: string,
  previousValue: PolicyValue
): PolicyValue => {
  const normalizedEditableValue = normalizeEditableWhitespace(nextValue);

  if (typeof previousValue === 'number') {
    const trimmedValue = normalizedEditableValue.trim();

    if (!trimmedValue) {
      return '';
    }

    const parsedValue = Number(normalizedEditableValue);
    return Number.isFinite(parsedValue) ? parsedValue : normalizedEditableValue;
  }

  if (typeof previousValue === 'boolean') {
    const normalizedValue = normalizedEditableValue.trim().toLowerCase();

    if (normalizedValue === 'true') {
      return true;
    }

    if (normalizedValue === 'false') {
      return false;
    }
  }

  return normalizedEditableValue;
};

const stopEventPropagation = (event: { stopPropagation: () => void }) => {
  event.stopPropagation();
};

const normalizeTemplateValuePath = (expression: string): string =>
  expression.trim().replace(/\.join\(\s*(['"]).*\1\s*\)$/, '');

const getTemplateVariablePaths = (template: string): Set<string> =>
  new Set(
    Array.from(
      template.matchAll(/\$\{expected_value_structure\.([^}]+)\}/g),
      (match) => normalizeTemplateValuePath(match[1] || '')
    ).filter((path) => path.length > 0)
  );

const pathReferencesTemplate = (
  path: string,
  templateVariablePaths: Set<string>
): boolean =>
  Array.from(templateVariablePaths).some(
    (templatePath) =>
      templatePath === path ||
      templatePath.startsWith(`${path}.`) ||
      path.startsWith(`${templatePath}.`)
  );

const getNestedPolicyValue = (
  value: PolicyValue | Record<string, PolicyValue>,
  path: string
): PolicyValue | undefined =>
  path
    .split('.')
    .filter((segment) => segment.trim().length > 0)
    .reduce<PolicyValue | undefined>((currentValue, segment) => {
      if (!isRecord(currentValue) && !Array.isArray(currentValue)) {
        return undefined;
      }

      return (currentValue as Record<string, PolicyValue>)[segment];
    }, value);

const setNestedPolicyValue = (
  value: Record<string, PolicyValue>,
  path: string,
  nextValue: PolicyValue
): Record<string, PolicyValue> => {
  const segments = path
    .split('.')
    .filter((segment) => segment.trim().length > 0);

  if (segments.length === 0) {
    return value;
  }

  const updateNode = (
    currentValue: PolicyValue | Record<string, PolicyValue>,
    segmentIndex: number
  ): PolicyValue => {
    const segment = segments[segmentIndex];
    const isLastSegment = segmentIndex === segments.length - 1;
    const currentNode = currentValue ?? {};

    if (Array.isArray(currentNode)) {
      const nextArray = [...currentNode];
      const arrayIndex = Number(segment);

      if (Number.isNaN(arrayIndex)) {
        return currentNode;
      }

      nextArray[arrayIndex] = isLastSegment
        ? nextValue
        : updateNode(nextArray[arrayIndex], segmentIndex + 1);
      return nextArray;
    }

    const nextObject = isRecord(currentNode) ? { ...currentNode } : {};

    nextObject[segment] = isLastSegment
      ? nextValue
      : updateNode(nextObject[segment], segmentIndex + 1);

    return nextObject;
  };

  return updateNode(value, 0) as Record<string, PolicyValue>;
};

const removeNestedPolicyArrayItem = (
  value: Record<string, PolicyValue>,
  path: string
): Record<string, PolicyValue> => {
  const segments = path
    .split('.')
    .filter((segment) => segment.trim().length > 0);

  if (segments.length === 0) {
    return value;
  }

  const updateNode = (
    currentValue: PolicyValue | Record<string, PolicyValue>,
    segmentIndex: number
  ): PolicyValue => {
    const segment = segments[segmentIndex];
    const isLastSegment = segmentIndex === segments.length - 1;
    const currentNode = currentValue ?? {};

    if (Array.isArray(currentNode)) {
      const arrayIndex = Number(segment);

      if (Number.isNaN(arrayIndex)) {
        return currentNode;
      }

      if (isLastSegment) {
        return currentNode.filter((_, index) => index !== arrayIndex);
      }

      const nextArray = [...currentNode];
      nextArray[arrayIndex] = updateNode(
        nextArray[arrayIndex],
        segmentIndex + 1
      );
      return nextArray;
    }

    const nextObject = isRecord(currentNode) ? { ...currentNode } : {};

    if (isLastSegment) {
      delete nextObject[segment];
      return nextObject;
    }

    nextObject[segment] = updateNode(nextObject[segment], segmentIndex + 1);
    return nextObject;
  };

  return updateNode(value, 0) as Record<string, PolicyValue>;
};

const resolveTemplateExpression = (
  expression: string,
  expectedValueStructure: Record<string, PolicyValue>
): string => {
  const trimmedExpression = expression.trim();
  const joinMatch = trimmedExpression.match(
    /^(.+)\.join\(\s*(['"])(.*)\2\s*\)$/
  );

  if (joinMatch) {
    const [, valuePath, , separator] = joinMatch;
    const resolvedValue = getNestedPolicyValue(
      expectedValueStructure,
      valuePath.trim()
    );

    if (!Array.isArray(resolvedValue)) {
      return '';
    }

    return resolvedValue
      .map((entry) => formatPrimitiveValue(entry))
      .join(separator);
  }

  const resolvedValue = getNestedPolicyValue(
    expectedValueStructure,
    trimmedExpression
  );

  if (resolvedValue === undefined) {
    return '';
  }

  return formatPrimitiveValue(resolvedValue);
};

const renderNormalizedRuleTemplate = (
  template: string,
  expectedValueStructure: Record<string, PolicyValue>
): string =>
  template.replace(
    /\$\{expected_value_structure\.([^}]+)\}/g,
    (_match, expression: string) =>
      resolveTemplateExpression(expression, expectedValueStructure)
  );

const normalizeAdjacentTemplateVariableSpacing = (template: string): string =>
  template.replace(/(\$\{[^}]+\})(?=\$\{)/g, '$1 ');

const processPolicyEditorStateOnce = (
  state: PolicyEditorState
): PolicyEditorState => {
  if (state.policyViewerProcessed) {
    return state;
  }

  let hasTemplateChanges = false;
  const nextPolicyCategories = state.policyCategories.map((candidate) => {
    const nextTemplate = normalizeAdjacentTemplateVariableSpacing(
      candidate.normalized_rule.normalized_rule_statement_template
    );

    if (
      nextTemplate ===
      candidate.normalized_rule.normalized_rule_statement_template
    ) {
      return candidate;
    }

    hasTemplateChanges = true;

    return {
      ...candidate,
      normalized_rule: {
        ...candidate.normalized_rule,
        normalized_rule_statement: renderNormalizedRuleTemplate(
          nextTemplate,
          candidate.normalized_rule.expected_value_structure
        ),
        normalized_rule_statement_template: nextTemplate
      }
    };
  });

  return {
    ...state,
    policyCategories: hasTemplateChanges
      ? nextPolicyCategories
      : state.policyCategories,
    policyViewerProcessed: true
  };
};

const getDirtyFieldContainerClassName = (isDirty: boolean) =>
  isDirty
    ? 'rounded-xl border border-amber-200 bg-amber-50/60 px-3 py-2.5'
    : '';

const getDirtyInputClassName = (isDirty: boolean) =>
  isDirty
    ? 'border-amber-300 bg-amber-50/70 focus:border-amber-400 focus:ring-amber-200'
    : '';

const toPolicyCandidateItem = (value: unknown): PolicyCandidateItem | null => {
  if (!isRecord(value) || !isRecord(value.normalized_rule)) {
    return null;
  }

  const normalizedRule = value.normalized_rule;
  const appliesToNormalized = isRecord(normalizedRule.applies_to_normalized)
    ? normalizedRule.applies_to_normalized
    : {};
  const expectedValueStructure = isRecord(
    normalizedRule.expected_value_structure
  )
    ? normalizedRule.expected_value_structure
    : {};
  const normalizationCaution = isRecord(normalizedRule.normalization_caution)
    ? normalizedRule.normalization_caution
    : {};
  const source = isRecord(normalizedRule.source) ? normalizedRule.source : {};

  const nextItem: PolicyCandidateItem = {
    candidate_id:
      typeof value.candidate_id === 'string' ? value.candidate_id : '',
    normalized_rule: {
      normalized_dimension:
        typeof normalizedRule.normalized_dimension === 'string'
          ? normalizedRule.normalized_dimension
          : '',
      primary_comparison_mechanic:
        typeof normalizedRule.primary_comparison_mechanic === 'string'
          ? normalizedRule.primary_comparison_mechanic
          : '',
      comparison_type:
        typeof normalizedRule.comparison_type === 'string'
          ? normalizedRule.comparison_type
          : '',
      expected_value_structure_type:
        typeof normalizedRule.expected_value_structure_type === 'string'
          ? normalizedRule.expected_value_structure_type
          : '',
      expected_value_structure: Object.fromEntries(
        Object.entries(expectedValueStructure).map(([key, entry]) => [
          key,
          toPolicyValue(entry)
        ])
      ),
      normalized_rule_statement:
        typeof normalizedRule.normalized_rule_statement === 'string'
          ? normalizedRule.normalized_rule_statement
          : '',
      normalized_rule_statement_template:
        typeof normalizedRule.normalized_rule_statement_template === 'string'
          ? normalizedRule.normalized_rule_statement_template
          : '',
      source: {
        source_text:
          typeof source.source_text === 'string' ? source.source_text : '',
        contextIds: toContextIdArray(source.contextIds, source.contextId),
        pageNumbers: toPageNumberArray(source.pageNumbers ?? source.pageNumber),
        sentenceIds: toStringArray(source.sentenceIds)
      },
      applies_to_normalized: {
        text: toStringArray(appliesToNormalized.text),
        node_refs: toStringArray(appliesToNormalized.node_refs)
      },
      normalization_caution: {
        level:
          typeof normalizationCaution.level === 'string'
            ? normalizationCaution.level
            : '',
        has_soft_or_hard_to_compare_wording: toBooleanValue(
          normalizationCaution.has_soft_or_hard_to_compare_wording
        ),
        notes: toStringArray(normalizationCaution.notes)
      }
    },
    embedding: Array.isArray(value.embedding) ? value.embedding : [],
    embeddingSourceText: Array.isArray(value.embeddingSourceText)
      ? value.embeddingSourceText
      : [],
    isUserCreated: toBooleanValue(
      'isUserCreated' in value ? value.isUserCreated : false
    ),
    edited: toBooleanValue('edited' in value ? value.edited : false),
    editedByUserId:
      typeof value.editedByUserId === 'string' ? value.editedByUserId : '',
    editedByName:
      typeof value.editedByName === 'string' ? value.editedByName : ''
  };

  const hasContent =
    nextItem.candidate_id.trim().length > 0 ||
    nextItem.normalized_rule.normalized_dimension.trim().length > 0 ||
    Object.keys(nextItem.normalized_rule.expected_value_structure).length > 0 ||
    nextItem.normalized_rule.normalized_rule_statement.trim().length > 0 ||
    nextItem.normalized_rule.source.source_text.trim().length > 0 ||
    nextItem.normalized_rule.applies_to_normalized.text.length > 0 ||
    nextItem.normalized_rule.normalization_caution.notes.length > 0 ||
    nextItem.embedding?.length > 0 ||
    nextItem.embeddingSourceText.length > 0;

  return hasContent ? nextItem : null;
};

const normalizePolicyCategories = (value: unknown): PolicyCandidateItem[] => {
  if (Array.isArray(value)) {
    return value.flatMap((item) => {
      const normalizedItem = toPolicyCandidateItem(item);
      return normalizedItem
        ? [normalizedItem]
        : normalizePolicyCategories(item);
    });
  }

  if (isRecord(value)) {
    const directMatch = toPolicyCandidateItem(value);
    if (directMatch) {
      return [directMatch];
    }

    return Object.values(value).flatMap((item) =>
      normalizePolicyCategories(item)
    );
  }

  return [];
};

const extractPolicyCategories = (data: unknown): PolicyCandidateItem[] =>
  normalizePolicyCategories(data);
//TODO remove later testing only
const renumberPolicyCandidates = (
  candidates: PolicyCandidateItem[]
): PolicyCandidateItem[] =>
  candidates.map((candidate, index) => ({
    ...candidate,
    candidate_id: String(index + 1)
  }));

const extractPolicyDetails = (
  data: unknown,
  fallbackDetails: PolicyDetailsState
): PolicyDetailsState => {
  const policyDetail =
    isRecord(data) && isRecord(data.policyDetail) ? data.policyDetail : null;
  const policyDetails =
    policyDetail && isRecord(policyDetail.policyDetails)
      ? policyDetail.policyDetails
      : isRecord(data) && isRecord(data.policyDetails)
        ? data.policyDetails
        : null;

  if (!policyDetails) {
    return fallbackDetails;
  }

  return {
    policyName:
      typeof policyDetails.policyName === 'string'
        ? policyDetails.policyName
        : fallbackDetails.policyName,
    version:
      typeof policyDetails.version === 'number'
        ? String(policyDetails.version)
        : typeof policyDetails.version === 'string'
          ? policyDetails.version
          : fallbackDetails.version,
    type:
      typeof policyDetails.type === 'string'
        ? policyDetails.type
        : fallbackDetails.type
  };
};

const getPolicyCandidateIdentity = (candidate: PolicyCandidateItem): string => {
  const trimmedCandidateId = candidate.candidate_id.trim();

  if (trimmedCandidateId.length > 0) {
    return `candidate:${trimmedCandidateId}`;
  }

  const source = candidate.normalized_rule.source;

  return `source:${source.contextIds.join('|')}::${source.sentenceIds.join('|')}::${source.source_text.trim()}`;
};

const mergePolicyCandidates = (
  currentCandidates: PolicyCandidateItem[],
  incomingCandidates: PolicyCandidateItem[]
): PolicyCandidateItem[] => {
  const nextCandidates = [...currentCandidates];
  const indexByIdentity = new Map<string, number>(
    nextCandidates.map((candidate, index) => [
      getPolicyCandidateIdentity(candidate),
      index
    ])
  );

  incomingCandidates.forEach((candidate) => {
    const candidateIdentity = getPolicyCandidateIdentity(candidate);
    const existingIndex = indexByIdentity.get(candidateIdentity);

    if (existingIndex === undefined) {
      indexByIdentity.set(candidateIdentity, nextCandidates.length);
      nextCandidates.push(candidate);
      return;
    }

    nextCandidates[existingIndex] = candidate;
  });

  return nextCandidates;
};

const withUserCreatedPolicyCandidate = (
  candidate: PolicyCandidateItem
): PolicyCandidateItem => ({
  ...candidate,
  isUserCreated: true
});

const withEditedPolicyCandidate = (
  candidate: PolicyCandidateItem,
  editedByUserId: string,
  editedByName: string
): PolicyCandidateItem => ({
  ...candidate,
  edited: true,
  editedByUserId,
  editedByName
});

const getErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message || error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Unable to load policy document.';
};

const isSiblingTaskAbortError = (error: unknown) => {
  if (error instanceof Error) {
    return error.message.includes('Sibling task failed');
  }

  if (!error || typeof error !== 'object') {
    return false;
  }

  return (
    'type' in error &&
    'reason' in error &&
    error.type === 'abort' &&
    error.reason === 'Sibling task failed'
  );
};

const isMissingPreparedAnnotationBatchError = (error: unknown) =>
  error instanceof Error &&
  error.message.includes('No prepared annotation batch found for');

const buildCandidateAnnotationReference = (
  item: PolicyCandidateItem,
  index: number,
  pageNumbers?: number[]
): PdfViewerAnnotationReference => {
  const contextIds = normalizeContextIds(
    item.normalized_rule.source.contextIds
  );

  return {
    candidateCardKey: getPolicyCandidateCardKey(item, index),
    candidateId: item.candidate_id.trim() || undefined,
    contextId: contextIds[0] ?? undefined,
    contextIds: contextIds.length > 0 ? contextIds : undefined,
    pageNumbers: normalizePageNumbers(
      pageNumbers ?? item.normalized_rule.source.pageNumbers
    ),
    sentenceIds: [...item.normalized_rule.source.sentenceIds],
    sourceText: item.normalized_rule.source.source_text.trim()
  };
};

const buildHighlightAllBatchSignature = (
  requests: PdfViewerAnnotationRequest[]
) =>
  JSON.stringify(
    requests.map((request) => ({
      pageNumbers: normalizePageNumbers([
        ...(request.pageNumbers ?? []),
        request.pageNumber
      ]),
      queries: normalizeHighlightAllQueries(request.query),
      reference: request.reference
        ? {
            candidateCardKey: request.reference.candidateCardKey ?? '',
            candidateId: request.reference.candidateId ?? '',
            contextIds: normalizeContextIds([
              ...(request.reference.contextIds ?? []),
              request.reference.contextId
            ]),
            pageNumbers: normalizePageNumbers(
              request.reference.pageNumbers ?? []
            ),
            sentenceIds: [...(request.reference.sentenceIds ?? [])].sort(),
            sourceText: request.reference.sourceText?.trim() ?? ''
          }
        : null
    }))
  );

const toContextPageNumberMap = (value: unknown): Map<string, number> => {
  const nextMap = new Map<string, number>();

  const assignPageNumber = (contextIdValue: unknown, pageValue: unknown) => {
    const pageNumber = toFiniteNumber(pageValue);

    if (pageNumber === null) {
      return;
    }

    normalizeContextIds(
      Array.isArray(contextIdValue)
        ? contextIdValue.map((value) =>
            typeof value === 'string' ? value : null
          )
        : [typeof contextIdValue === 'string' ? contextIdValue : null]
    ).forEach((contextId) => {
      nextMap.set(contextId, pageNumber);
    });
  };

  const sourceValue =
    isRecord(value) && isRecord(value.contextPageMap)
      ? value.contextPageMap
      : value;

  if (Array.isArray(sourceValue)) {
    sourceValue.forEach((entry) => {
      if (!isRecord(entry)) {
        return;
      }

      assignPageNumber(
        entry.contextIds ?? entry.contextId,
        entry.pageNumber ?? entry.page ?? entry.page_index
      );
    });

    return nextMap;
  }

  if (!isRecord(sourceValue)) {
    return nextMap;
  }

  Object.entries(sourceValue).forEach(([contextId, pageValue]) => {
    assignPageNumber(contextId, pageValue);
  });

  return nextMap;
};

const getPolicyCandidateSourcePageNumbers = (
  item: PolicyCandidateItem,
  contextPageMap: Map<string, number>
) => {
  const explicitPageNumbers = normalizePageNumbers(
    item.normalized_rule.source.pageNumbers
  );

  if (explicitPageNumbers.length > 0) {
    return explicitPageNumbers;
  }

  return normalizePageNumbers(
    item.normalized_rule.source.contextIds.map(
      (contextId) => contextPageMap.get(contextId) ?? null
    )
  );
};

const withPolicyCandidateSourcePageNumbers = (
  item: PolicyCandidateItem,
  pageNumbers: number[]
): PolicyCandidateItem => ({
  ...item,
  normalized_rule: {
    ...item.normalized_rule,
    source: {
      ...item.normalized_rule.source,
      pageNumbers: normalizePageNumbers(pageNumbers)
    }
  }
});

const getEditorStateFromSavedPolicy = (
  value: Record<string, unknown>,
  fallbackState: PolicyEditorState
): PolicyEditorState => {
  return {
    policyDetails: {
      policyName:
        typeof value.policyName === 'string' && value.policyName.trim()
          ? value.policyName
          : fallbackState.policyDetails.policyName,
      version:
        typeof value.version === 'number'
          ? String(value.version)
          : typeof value.version === 'string'
            ? value.version
            : fallbackState.policyDetails.version,
      type:
        typeof value.type === 'string'
          ? value.type
          : fallbackState.policyDetails.type
    },
    policyCategories: fallbackState.policyCategories,
    policyViewerProcessed: fallbackState.policyViewerProcessed
  };
};

const PolicyDocumentViewer: React.FC<AnalyzedContractViewerProps> = ({
  clickedHighlightSentenceIds,
  clickedHighlightSelectionKey,
  currentPdfPage,
  manualBenchmarkSelection,
  onSelectSentenceIds,
  onDebugHighlightSelection,
  onPreserveHighlightsOnPageChangeChange,
  onLoadingStateChange,
  showPdf,
  onToggleShowPdf,
  reducedWidthMode = false,
  selectedAnnotationReference,
  viewerRef,
  fallbackPageItems,
  setFallbackPageItems
}) => {
  const { auth, token, user } = useContext(UserContext);
  const { allDocuments, setCurrentDocId } = useContext(DocumentContext);
  const {
    policies,
    getPoliciesLoading,
    getPolicies,
    addPolicy,
    addPolicyLoading,
    updatePolicy,
    updatePolicyLoading
  } = usePolicyContext();
  const { setAlert } = useAlert();
  const candidateCardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const candidateListContainerRef = useRef<HTMLDivElement | null>(null);
  const candidateStickyControlsRef = useRef<HTMLDivElement | null>(null);
  const headerActionsMenuRef = useRef<HTMLDivElement | null>(null);
  const activeHighlightAllSelectionRequestRef =
    useRef<PdfViewerAnnotationRequest | null>(null);
  const highlightSelectionActionInFlightRef = useRef(false);
  const lastProcessedHighlightSelectionKeyRef = useRef<number>(0);
  const lastProcessedManualBenchmarkSelectionKeyRef = useRef<number>(0);
  const lastProcessedAnnotationSelectionKeyRef = useRef<string | null>(null);
  const highlightAllPreparationGenerationRef = useRef(0);
  const highlightAllPagePreparationPromisesRef = useRef<
    Map<
      number,
      {
        generation: number;
        promise: Promise<void>;
        signature: string;
      }
    >
  >(new Map());
  const highlightAllAppliedPageSignaturesRef = useRef<Record<number, string>>(
    {}
  );
  const highlightAllPreparedPageSignaturesRef = useRef<Record<number, string>>(
    {}
  );
  const highlightAllPageChangeVersionRef = useRef(0);
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [policyCategories, setPolicyCategories] = useState<
    PolicyCandidateItem[]
  >([]);
  const [policyViewerProcessed, setPolicyViewerProcessed] =
    useState<boolean>(true);
  const [policyDetails, setPolicyDetails] =
    useState<PolicyDetailsState>(EMPTY_POLICY_DETAILS);
  const [sourceState, setSourceState] = useState<PolicyEditorState | null>(
    null
  );
  const [initialEditorState, setInitialEditorState] =
    useState<PolicyEditorState | null>(null);
  const [editorInitialized, setEditorInitialized] = useState<boolean>(false);
  const [policyLookupComplete, setPolicyLookupComplete] =
    useState<boolean>(false);
  const [createPolicyLoading, setCreatePolicyLoading] =
    useState<boolean>(false);
  const [activeCandidateCardKey, setActiveCandidateCardKey] = useState<
    string | null
  >(null);
  const [
    activeHighlightAllSelectionCardKey,
    setActiveHighlightAllSelectionCardKey
  ] = useState<string | null>(null);
  const [showAllItemsActive, setShowAllItemsActive] = useState<boolean>(false);
  const [manualBenchmarkDraft, setManualBenchmarkDraft] =
    useState<ManualBenchmarkDraft | null>(null);
  const [manualBenchmarkLoading, setManualBenchmarkLoading] =
    useState<boolean>(false);
  const [clearAnnotationsLoading, setClearAnnotationsLoading] =
    useState<boolean>(false);
  const [highlightAllLoading, setHighlightAllLoading] =
    useState<boolean>(false);
  const [isHeaderActionsMenuOpen, setIsHeaderActionsMenuOpen] =
    useState<boolean>(false);
  const [selectedDimensionFilter, setSelectedDimensionFilter] =
    useState<OptionDefinition | null>(null);
  const [selectedBenchmarkSourceFilter, setSelectedBenchmarkSourceFilter] =
    useState<OptionDefinition | null>(null);
  const [filterByCurrentPage, setFilterByCurrentPage] =
    useState<boolean>(false);
  const [
    highlightAllAppliedPageSignatures,
    setHighlightAllAppliedPageSignatures
  ] = useState<Record<number, string>>({});
  const [
    highlightAllPreparedPageSignatures,
    setHighlightAllPreparedPageSignatures
  ] = useState<Record<number, string>>({});
  const [
    savedPreparedPageAnnotationBatches,
    setSavedPreparedPageAnnotationBatches
  ] = useState<SavedPreparedPageAnnotationBatches>({});
  const [manualBenchmarkError, setManualBenchmarkError] = useState<string>('');
  const [contextPageMap, setContextPageNumberById] = useState<
    Map<string, number>
  >(() => new Map());

  useEffect(() => {
    highlightAllAppliedPageSignaturesRef.current =
      highlightAllAppliedPageSignatures;
  }, [highlightAllAppliedPageSignatures]);

  useEffect(() => {
    highlightAllPreparedPageSignaturesRef.current =
      highlightAllPreparedPageSignatures;
  }, [highlightAllPreparedPageSignatures]);

  useEffect(() => {
    if (!isHeaderActionsMenuOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;

      if (headerActionsMenuRef.current?.contains(target)) {
        return;
      }

      setIsHeaderActionsMenuOpen(false);
    };

    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsHeaderActionsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isHeaderActionsMenuOpen]);

  const documentId = searchParams.get('documentId');
  const requestId = searchParams.get('requestId') || undefined;
  const parseTypeFromQuery = searchParams.get('parseType') || undefined;

  useUnsavedChangesWarning(
    manualBenchmarkLoading,
    'Benchmark rule creation is still in progress. Are you sure you want to leave this page?'
  );

  const currentDocument = useMemo(
    () => allDocuments.find((document) => document._id === documentId) ?? null,
    [allDocuments, documentId]
  );
  const isPdfVisible = showPdf ?? true;

  const existingPolicy = useMemo(
    () => policies.find((policy) => policy.documentId === documentId) ?? null,
    [documentId, policies]
  );

  const saveLoading =
    addPolicyLoading || updatePolicyLoading || manualBenchmarkLoading;

  const exitHighlightAllMode = useCallback(
    (clearSelectedSentenceIds = true) => {
      highlightAllPageChangeVersionRef.current += 1;
      activeHighlightAllSelectionRequestRef.current = null;
      setActiveHighlightAllSelectionCardKey(null);
      setShowAllItemsActive(false);
      setHighlightAllAppliedPageSignatures({});
      highlightAllAppliedPageSignaturesRef.current = {};
      onPreserveHighlightsOnPageChangeChange?.(false);

      if (clearSelectedSentenceIds) {
        onSelectSentenceIds?.([]);
      }
    },
    [onPreserveHighlightsOnPageChangeChange, onSelectSentenceIds]
  );

  const scrollCandidateCardIntoView = useCallback((cardKey: string) => {
    const cardElement = candidateCardRefs.current[cardKey];
    const containerElement = isVerticallyScrollable(
      candidateListContainerRef.current
    )
      ? candidateListContainerRef.current
      : getNearestScrollContainer(cardElement);

    if (!cardElement) {
      return;
    }

    if (!containerElement) {
      cardElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      return;
    }

    const containerRect = containerElement.getBoundingClientRect();
    const cardRect = cardElement.getBoundingClientRect();
    const stickyOffset =
      candidateStickyControlsRef.current?.getBoundingClientRect().height ?? 0;
    const cardTop =
      cardRect.top - containerRect.top + containerElement.scrollTop;
    const cardBottom = cardTop + cardRect.height;
    const visibleTop = containerElement.scrollTop + stickyOffset;
    const visibleBottom =
      containerElement.scrollTop + containerElement.clientHeight;
    const scrollMargin = 16;

    let targetScrollTop = containerElement.scrollTop;

    if (cardTop < visibleTop + scrollMargin) {
      targetScrollTop = cardTop - stickyOffset - scrollMargin;
    } else if (cardBottom > visibleBottom - scrollMargin) {
      targetScrollTop =
        cardBottom -
        containerElement.clientHeight +
        stickyOffset +
        scrollMargin;
    } else {
      return;
    }

    containerElement.scrollTo({
      top: Math.max(targetScrollTop, 0),
      behavior: 'smooth'
    });
  }, []);

  const currentEditorState = useMemo(
    () => ({
      policyDetails,
      policyCategories,
      policyViewerProcessed
    }),
    [policyCategories, policyDetails, policyViewerProcessed]
  );

  const isDirty =
    Boolean(initialEditorState) &&
    !deepEqual(currentEditorState, initialEditorState);
  const isPolicyDetailsFieldDirty = useCallback(
    (field: keyof PolicyDetailsState) =>
      !initialEditorState ||
      !deepEqual(policyDetails[field], initialEditorState.policyDetails[field]),
    [initialEditorState, policyDetails]
  );
  const isExpectedValuePathDirty = useCallback(
    (candidateIndex: number, path: string, currentValue: PolicyValue) => {
      const initialCandidate =
        initialEditorState?.policyCategories[candidateIndex];

      if (!initialCandidate) {
        return true;
      }

      const initialValue = getNestedPolicyValue(
        initialCandidate.normalized_rule.expected_value_structure,
        path
      );

      return !deepEqual(currentValue, initialValue);
    },
    [initialEditorState]
  );

  const removedCandidateCount = useMemo(() => {
    if (!initialEditorState) {
      return 0;
    }

    return Math.max(
      initialEditorState.policyCategories.length - policyCategories.length,
      0
    );
  }, [initialEditorState, policyCategories.length]);

  const selectedDimensionFilterValue = selectedDimensionFilter?.value ?? '';
  const selectedBenchmarkSourceFilterValue =
    selectedBenchmarkSourceFilter?.value ?? '';
  const normalizedCurrentPdfPage =
    typeof currentPdfPage === 'number' ? currentPdfPage : null;
  const hasCurrentPdfPage = normalizedCurrentPdfPage !== null;
  const hasUserCreatedBenchmarks = useMemo(
    () =>
      policyCategories.some(isUserCreatedPolicyCandidate) ||
      Boolean(manualBenchmarkDraft),
    [manualBenchmarkDraft, policyCategories]
  );
  const dimensionFilterOptions = useMemo<OptionDefinition[]>(() => {
    const optionsByValue = new Map<string, string>();

    policyCategories.forEach((item) => {
      const dimensionKey = item.normalized_rule.normalized_dimension.trim();
      const groupKey = getPolicyCandidateDimensionKey(item);

      if (!optionsByValue.has(groupKey)) {
        optionsByValue.set(groupKey, getNormalizedDimensionLabel(dimensionKey));
      }
    });

    return Array.from(optionsByValue.entries())
      .sort(([leftValue, leftLabel], [rightValue, rightLabel]) => {
        const labelComparison = leftLabel.localeCompare(rightLabel);

        return labelComparison !== 0
          ? labelComparison
          : leftValue.localeCompare(rightValue);
      })
      .map(([value, label]) => ({
        label,
        value
      }));
  }, [policyCategories]);
  const benchmarkSourceFilterOptions = useMemo<OptionDefinition[]>(
    () =>
      hasUserCreatedBenchmarks
        ? [
            {
              label: 'User Created Benchmarks',
              value: USER_CREATED_BENCHMARK_FILTER_VALUE
            }
          ]
        : [],
    [hasUserCreatedBenchmarks]
  );
  const shouldShowManualBenchmarkDraft = Boolean(
    manualBenchmarkDraft &&
      (!selectedBenchmarkSourceFilterValue ||
        selectedBenchmarkSourceFilterValue ===
          USER_CREATED_BENCHMARK_FILTER_VALUE) &&
      (!filterByCurrentPage ||
        (hasCurrentPdfPage
          ? manualBenchmarkDraft.pageNumbers.includes(normalizedCurrentPdfPage)
          : false))
  );
  const filteredPolicyCandidateEntries = useMemo(
    () =>
      policyCategories
        .map((item, index) => ({
          index,
          item
        }))
        .filter(({ item }) =>
          selectedDimensionFilterValue
            ? getPolicyCandidateDimensionKey(item) ===
              selectedDimensionFilterValue
            : true
        )
        .filter(({ item }) =>
          selectedBenchmarkSourceFilterValue ===
          USER_CREATED_BENCHMARK_FILTER_VALUE
            ? isUserCreatedPolicyCandidate(item)
            : true
        )
        .filter(({ item }) =>
          filterByCurrentPage
            ? hasCurrentPdfPage
              ? getPolicyCandidateSourcePageNumbers(
                  item,
                  contextPageMap
                ).includes(normalizedCurrentPdfPage)
              : false
            : true
        ),
    [
      contextPageMap,
      filterByCurrentPage,
      hasCurrentPdfPage,
      normalizedCurrentPdfPage,
      policyCategories,
      selectedBenchmarkSourceFilterValue,
      selectedDimensionFilterValue
    ]
  );
  const groupedPolicyCategories = useMemo<PolicyCandidateGroup[]>(() => {
    const groups = new Map<string, PolicyCandidateGroup>();

    filteredPolicyCandidateEntries.forEach(({ item, index }) => {
      const dimensionKey = item.normalized_rule.normalized_dimension.trim();
      const groupKey = getPolicyCandidateDimensionKey(item);

      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          dimensionKey: groupKey,
          dimensionLabel: getNormalizedDimensionLabel(dimensionKey),
          items: []
        });
      }

      groups.get(groupKey)?.items.push({
        index,
        item
      });
    });

    return Array.from(groups.values());
  }, [filteredPolicyCandidateEntries]);
  const allCandidateSourceSentenceIds = useMemo(
    () =>
      Array.from(
        new Set([
          ...filteredPolicyCandidateEntries.flatMap(
            ({ item }) => item.normalized_rule.source.sentenceIds
          )
        ])
      ),
    [filteredPolicyCandidateEntries]
  );
  const candidateCardKeyBySentenceId = useMemo(() => {
    const nextMap: Record<string, string> = {};

    filteredPolicyCandidateEntries.forEach(({ item, index }) => {
      const cardKey = getPolicyCandidateCardKey(item, index);

      item.normalized_rule.source.sentenceIds.forEach((sentenceId) => {
        if (!nextMap[sentenceId]) {
          nextMap[sentenceId] = cardKey;
        }
      });
    });

    return nextMap;
  }, [filteredPolicyCandidateEntries]);
  const highlightAllAnnotationRequests = useMemo(
    () =>
      filteredPolicyCandidateEntries.reduce<PdfViewerAnnotationRequest[]>(
        (requests, { item, index }) => {
          const query = item.normalized_rule.source.source_text.trim();
          const pageNumbers = getPolicyCandidateSourcePageNumbers(
            item,
            contextPageMap
          );

          if (!query) {
            return requests;
          }

          requests.push({
            pageNumber: pageNumbers[0],
            pageNumbers,
            query,
            reference: buildCandidateAnnotationReference(
              item,
              index,
              pageNumbers
            )
          });

          return requests;
        },
        []
      ),
    [contextPageMap, filteredPolicyCandidateEntries]
  );
  const highlightAllBatchSignature = useMemo(
    () => buildHighlightAllBatchSignature(highlightAllAnnotationRequests),
    [highlightAllAnnotationRequests]
  );
  const highlightAllAnnotationRequestsByPage = useMemo(() => {
    const nextMap = new Map<number, PdfViewerAnnotationRequest[]>();

    highlightAllAnnotationRequests.forEach((request) => {
      const requestPageNumbers =
        request.pageNumbers?.length && request.pageNumbers.length > 0
          ? request.pageNumbers
          : request.pageNumber
            ? [request.pageNumber]
            : [];

      requestPageNumbers.forEach((pageNumber) => {
        const currentRequests = nextMap.get(pageNumber) ?? [];

        currentRequests.push(request);
        nextMap.set(pageNumber, currentRequests);
      });
    });

    return nextMap;
  }, [highlightAllAnnotationRequests]);
  const highlightAllBatchSignatureByPage = useMemo(
    () =>
      new Map(
        Array.from(highlightAllAnnotationRequestsByPage.entries()).map(
          ([pageNumber, requests]) => [
            pageNumber,
            buildHighlightAllBatchSignature(requests)
          ]
        )
      ),
    [highlightAllAnnotationRequestsByPage]
  );
  const getHighlightAllRequestsForPage = useCallback(
    (pageNumber: number) =>
      highlightAllAnnotationRequestsByPage.get(pageNumber) ??
      highlightAllAnnotationRequests,
    [highlightAllAnnotationRequests, highlightAllAnnotationRequestsByPage]
  );
  const getHighlightAllBatchSignatureForPage = useCallback(
    (pageNumber: number) =>
      highlightAllBatchSignatureByPage.get(pageNumber) ??
      `${highlightAllBatchSignature}::page-${pageNumber}`,
    [highlightAllBatchSignature, highlightAllBatchSignatureByPage]
  );
  const visibleCandidateCount =
    filteredPolicyCandidateEntries.length +
    (shouldShowManualBenchmarkDraft ? 1 : 0);
  const emptyCandidateMessage = useMemo(() => {
    if (!documentId) {
      return 'Policy candidates will appear here.';
    }

    if (filterByCurrentPage && !hasCurrentPdfPage) {
      return 'No current PDF page is available for filtering.';
    }

    if (
      filterByCurrentPage &&
      selectedDimensionFilterValue &&
      selectedBenchmarkSourceFilterValue === USER_CREATED_BENCHMARK_FILTER_VALUE
    ) {
      return 'No user created benchmarks found for the current page and selected dimension.';
    }

    if (filterByCurrentPage && selectedDimensionFilterValue) {
      return 'No policy candidates found for the current page and selected dimension.';
    }

    if (
      filterByCurrentPage &&
      selectedBenchmarkSourceFilterValue === USER_CREATED_BENCHMARK_FILTER_VALUE
    ) {
      return 'No user created benchmarks found for the current page.';
    }

    if (filterByCurrentPage) {
      return 'No policy candidates found for the current page.';
    }

    if (
      selectedDimensionFilterValue &&
      selectedBenchmarkSourceFilterValue === USER_CREATED_BENCHMARK_FILTER_VALUE
    ) {
      return 'No user created benchmarks found for the selected dimension.';
    }

    if (selectedDimensionFilterValue) {
      return 'No policy candidates found for the selected dimension.';
    }

    if (
      selectedBenchmarkSourceFilterValue === USER_CREATED_BENCHMARK_FILTER_VALUE
    ) {
      return 'No user created benchmarks found.';
    }

    return 'No policy candidates found.';
  }, [
    documentId,
    filterByCurrentPage,
    hasCurrentPdfPage,
    selectedBenchmarkSourceFilterValue,
    selectedDimensionFilterValue
  ]);

  useEffect(() => {
    if (
      benchmarkSourceFilterOptions.length > 0 ||
      !selectedBenchmarkSourceFilter
    ) {
      return;
    }

    setSelectedBenchmarkSourceFilter(null);
  }, [benchmarkSourceFilterOptions.length, selectedBenchmarkSourceFilter]);

  const setActiveCandidateFromReference = useCallback(
    (
      annotationReference: PdfViewerAnnotationReference,
      options: {
        activateCard?: boolean;
      } = {}
    ) => {
      const { activateCard = true } = options;
      if (
        annotationReference.candidateCardKey === MANUAL_BENCHMARK_CARD_KEY &&
        manualBenchmarkDraft
      ) {
        if (activateCard) {
          setActiveCandidateCardKey(MANUAL_BENCHMARK_CARD_KEY);
        }

        window.requestAnimationFrame(() => {
          scrollCandidateCardIntoView(MANUAL_BENCHMARK_CARD_KEY);
        });

        return true;
      }

      const normalizedCandidateId = annotationReference.candidateId?.trim();
      const normalizedSourceText = annotationReference.sourceText?.trim();
      const normalizedReferenceContextIds = normalizeContextIds([
        ...(annotationReference.contextIds ?? []),
        annotationReference.contextId
      ]);
      const normalizedReferenceContextIdSet = new Set(
        normalizedReferenceContextIds
      );
      const matchedIndex = policyCategories.findIndex((item, index) => {
        const cardKey = getPolicyCandidateCardKey(item, index);

        if (cardKey === annotationReference.candidateCardKey) {
          return true;
        }

        if (
          normalizedCandidateId &&
          item.candidate_id.trim() === normalizedCandidateId
        ) {
          return true;
        }

        return Boolean(
          normalizedReferenceContextIdSet.size > 0 &&
            normalizedSourceText &&
            item.normalized_rule.source.contextIds.some((contextId) =>
              normalizedReferenceContextIdSet.has(contextId)
            ) &&
            item.normalized_rule.source.source_text.trim() ===
              normalizedSourceText
        );
      });

      if (matchedIndex < 0) {
        return false;
      }

      const matchedCandidate = policyCategories[matchedIndex];
      const matchedCardKey = getPolicyCandidateCardKey(
        matchedCandidate,
        matchedIndex
      );
      // const sentenceIds = annotationReference.sentenceIds?.length
      //   ? annotationReference.sentenceIds
      //   : matchedCandidate.normalized_rule.source.sentenceIds;

      if (activateCard) {
        setActiveCandidateCardKey(matchedCardKey);
      }
      // onSelectSentenceIds?.(sentenceIds);

      window.requestAnimationFrame(() => {
        scrollCandidateCardIntoView(matchedCardKey);
      });

      return true;
    },
    [
      exitHighlightAllMode,
      manualBenchmarkDraft,
      onSelectSentenceIds,
      policyCategories,
      scrollCandidateCardIntoView
    ]
  );

  useEffect(() => {
    if (!documentId) {
      return;
    }

    setCurrentDocId(documentId);
  }, [documentId, setCurrentDocId]);

  useEffect(() => {
    setEditorInitialized(false);
    setPolicyLookupComplete(false);
    setInitialEditorState(null);
    setSourceState(null);
    setPolicyDetails(EMPTY_POLICY_DETAILS);
    setPolicyCategories([]);
    setPolicyViewerProcessed(true);
    setContextPageNumberById(new Map());
    setError('');
    setActiveCandidateCardKey(null);
    setActiveHighlightAllSelectionCardKey(null);
    setShowAllItemsActive(false);
    setHighlightAllAppliedPageSignatures({});
    setHighlightAllPreparedPageSignatures({});
    setSavedPreparedPageAnnotationBatches({});
    setSelectedDimensionFilter(null);
    setSelectedBenchmarkSourceFilter(null);
    setFilterByCurrentPage(false);
    setManualBenchmarkDraft(null);
    setManualBenchmarkError('');
    setManualBenchmarkLoading(false);
    lastProcessedHighlightSelectionKeyRef.current = 0;
    lastProcessedManualBenchmarkSelectionKeyRef.current = 0;
    lastProcessedAnnotationSelectionKeyRef.current = null;
    activeHighlightAllSelectionRequestRef.current = null;
    highlightSelectionActionInFlightRef.current = false;
    highlightAllPreparationGenerationRef.current += 1;
    highlightAllPagePreparationPromisesRef.current.clear();
    highlightAllAppliedPageSignaturesRef.current = {};
    highlightAllPreparedPageSignaturesRef.current = {};
    highlightAllPageChangeVersionRef.current += 1;
    onPreserveHighlightsOnPageChangeChange?.(false);
    onSelectSentenceIds?.([]);
  }, [documentId, onPreserveHighlightsOnPageChangeChange, onSelectSentenceIds]);

  useEffect(() => {
    let cancelled = false;

    const loadPoliciesForDocument = async () => {
      if (!token || !documentId) {
        if (!cancelled) {
          setPolicyLookupComplete(true);
        }
        return;
      }

      await getPolicies();

      if (!cancelled) {
        setPolicyLookupComplete(true);
      }
    };

    loadPoliciesForDocument();

    return () => {
      cancelled = true;
    };
    // `getPolicies` is recreated by context updates; only refetch on document/token change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, token]);

  useEffect(() => {
    let cancelled = false;

    const loadPolicyJson = async () => {
      if (!token || !documentId || !policyLookupComplete) {
        return;
      }

      const policyJsonDocumentId = existingPolicy?._id || documentId;

      setLoading(true);
      setError('');

      let lastError = 'Unable to fetch the policy JSON file.';

      try {
        const presignedUrl = await axios.post(
          `${process.env.REACT_APP_KORU_API}/get-presigned-url?type=getPolicyJson`,
          { documentId },
          auth
        );
        const downloadUrl = presignedUrl?.data?.url;

        if (!downloadUrl || downloadUrl === 'locked') {
          throw new Error('No policy JSON presigned URL was returned.');
        }

        const response = await axios.get(downloadUrl, {
          responseType: 'json'
        });

        if (cancelled) {
          return;
        }

        try {
          const contextPageMapResponse = await axios.post(
            `${process.env.REACT_APP_KORU_API}/document-contexts?type=contextPageMap`,
            { documentId },
            auth
          );

          if (!cancelled) {
            setContextPageNumberById(
              toContextPageNumberMap(contextPageMapResponse.data.result)
            );
          }
        } catch (_error) {
          if (!cancelled) {
            setContextPageNumberById(new Map());
          }
        }
        setFallbackPageItems(
          groupPdfFallbackTextItemsByPage(response.data?.pageItems)
        );
        setSavedPreparedPageAnnotationBatches(
          normalizeSavedPreparedPageAnnotationBatches(
            response.data?.preparedPageAnnotations
          )
        );
        const fallbackDetails = {
          policyName: getPolicyNameFromFileName(
            currentDocument?.documentFileName
          ),
          version: currentDocument?.documentMetaData?.version || '',
          type: currentDocument?.documentType || 'policy_document'
        };
        const dataToUse = response.data.benchmark_4b_result
          ? response.data.benchmark_4b_result
          : response.data;
        const initialPolicyCategories = renumberPolicyCandidates(
          extractPolicyCategories(dataToUse)
        );
        const initialState = {
          policyCategories: initialPolicyCategories,
          policyDetails: extractPolicyDetails(
            response.data.benchmark_4b_results,
            fallbackDetails
          ),
          policyViewerProcessed:
            isRecord(response.data) &&
            response.data[POLICY_VIEWER_PROCESSED_FLAG] === true
        };

        setSourceState(initialState);
        setInitialEditorState(cloneValue(initialState));
        setLoading(false);

        return;
      } catch (fetchError) {
        lastError = getErrorMessage(fetchError);
      }

      if (!cancelled) {
        setSourceState(null);
        setPolicyCategories([]);
        setPolicyDetails(EMPTY_POLICY_DETAILS);
        setPolicyViewerProcessed(true);
        setSavedPreparedPageAnnotationBatches({});
        setError(lastError);
        setLoading(false);
      }
    };

    loadPolicyJson();

    return () => {
      cancelled = true;
    };
  }, [
    auth,
    currentDocument?.documentFileName,
    currentDocument?.documentType,
    currentDocument?.documentMetaData?.version,
    documentId,
    existingPolicy?._id,
    policyLookupComplete,
    token
  ]);

  useEffect(() => {
    onLoadingStateChange?.(loading);
  }, [loading, onLoadingStateChange]);

  useEffect(() => {
    if (!showAllItemsActive || !clickedHighlightSentenceIds?.length) {
      return;
    }

    if (
      !clickedHighlightSelectionKey ||
      clickedHighlightSelectionKey <=
        lastProcessedHighlightSelectionKeyRef.current
    ) {
      return;
    }

    const nextCardKey = clickedHighlightSentenceIds
      .map((sentenceId) => candidateCardKeyBySentenceId[sentenceId])
      .find((cardKey): cardKey is string => Boolean(cardKey));

    if (!nextCardKey) {
      return;
    }

    lastProcessedHighlightSelectionKeyRef.current =
      clickedHighlightSelectionKey;

    const animationFrame = window.requestAnimationFrame(() => {
      scrollCandidateCardIntoView(nextCardKey);
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [
    candidateCardKeyBySentenceId,
    clickedHighlightSentenceIds,
    clickedHighlightSelectionKey,
    scrollCandidateCardIntoView,
    showAllItemsActive
  ]);

  useEffect(() => {
    if (!manualBenchmarkSelection) {
      return;
    }

    const trimmedSourceText = manualBenchmarkSelection.sourceText.trim();
    if (!trimmedSourceText) {
      return;
    }

    if (
      manualBenchmarkSelection.selectionKey <=
      lastProcessedManualBenchmarkSelectionKeyRef.current
    ) {
      return;
    }

    if (manualBenchmarkLoading) {
      return;
    }

    lastProcessedManualBenchmarkSelectionKeyRef.current =
      manualBenchmarkSelection.selectionKey;
    setManualBenchmarkDraft({
      pageNumbers: normalizePageNumbers(manualBenchmarkSelection.pageNumbers),
      sourceText: trimmedSourceText,
      selectionKey: manualBenchmarkSelection.selectionKey
    });
    setManualBenchmarkError('');
    exitHighlightAllMode();
    setActiveCandidateCardKey(MANUAL_BENCHMARK_CARD_KEY);
  }, [exitHighlightAllMode, manualBenchmarkLoading, manualBenchmarkSelection]);

  useEffect(() => {
    if (!manualBenchmarkDraft) {
      return;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      scrollCandidateCardIntoView(MANUAL_BENCHMARK_CARD_KEY);
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [manualBenchmarkDraft, scrollCandidateCardIntoView]);

  useEffect(() => {
    if (!selectedAnnotationReference) {
      return;
    }

    if (
      selectedAnnotationReference.selectionKey ===
      lastProcessedAnnotationSelectionKeyRef.current
    ) {
      return;
    }

    if (highlightSelectionActionInFlightRef.current) {
      lastProcessedAnnotationSelectionKeyRef.current =
        selectedAnnotationReference.selectionKey;
      return;
    }

    if (showAllItemsActive) {
      setActiveCandidateFromReference(selectedAnnotationReference.reference, {
        activateCard: false
      });
      lastProcessedAnnotationSelectionKeyRef.current =
        selectedAnnotationReference.selectionKey;
      return;
    }

    const matched = setActiveCandidateFromReference(
      selectedAnnotationReference.reference
    );

    if (!matched) {
      return;
    }

    lastProcessedAnnotationSelectionKeyRef.current =
      selectedAnnotationReference.selectionKey;
  }, [
    selectedAnnotationReference,
    setActiveCandidateFromReference,
    showAllItemsActive
  ]);

  useEffect(() => {
    highlightAllPreparationGenerationRef.current += 1;
    highlightAllPagePreparationPromisesRef.current.clear();
    setHighlightAllAppliedPageSignatures({});
    setHighlightAllPreparedPageSignatures({});
    highlightAllAppliedPageSignaturesRef.current = {};
    highlightAllPreparedPageSignaturesRef.current = {};
    highlightAllPageChangeVersionRef.current += 1;
  }, [highlightAllBatchSignature]);

  useEffect(() => {
    viewerRef?.current?.hydratePreparedAnnotationBatches?.(
      buildPreparedAnnotationBatchCacheMap(savedPreparedPageAnnotationBatches)
    );
  }, [savedPreparedPageAnnotationBatches, viewerRef]);

  useEffect(() => {
    setSavedPreparedPageAnnotationBatches((previousValue) => {
      const nextValue = filterSavedPreparedPageAnnotationBatches(
        previousValue,
        highlightAllBatchSignatureByPage
      );

      return deepEqual(previousValue, nextValue) ? previousValue : nextValue;
    });
  }, [highlightAllBatchSignatureByPage]);

  useEffect(() => {
    const nextValue = buildPreparedPageSignaturesFromSavedBatches(
      savedPreparedPageAnnotationBatches,
      highlightAllBatchSignatureByPage
    );

    setHighlightAllPreparedPageSignatures((previousValue) => {
      if (deepEqual(previousValue, nextValue)) {
        return previousValue;
      }

      highlightAllPreparedPageSignaturesRef.current = nextValue;
      return nextValue;
    });
  }, [highlightAllBatchSignatureByPage, savedPreparedPageAnnotationBatches]);

  useEffect(() => {
    if (
      !sourceState ||
      getPoliciesLoading ||
      !policyLookupComplete ||
      editorInitialized
    ) {
      return;
    }

    const nextState =
      existingPolicy && typeof existingPolicy === 'object'
        ? getEditorStateFromSavedPolicy(
            existingPolicy as Record<string, unknown>,
            sourceState
          )
        : sourceState;

    const clonedState = cloneValue(processPolicyEditorStateOnce(nextState));

    setPolicyDetails(clonedState.policyDetails);
    setPolicyCategories(clonedState.policyCategories);
    setPolicyViewerProcessed(clonedState.policyViewerProcessed);
    setEditorInitialized(true);
  }, [
    editorInitialized,
    existingPolicy,
    getPoliciesLoading,
    policyLookupComplete,
    sourceState
  ]);

  useEffect(() => {
    if (!sourceState || !editorInitialized || existingPolicy || isDirty) {
      return;
    }

    setPolicyDetails((previousValue) => {
      const nextValue = {
        policyName:
          previousValue.policyName || sourceState.policyDetails.policyName,
        version: previousValue.version || sourceState.policyDetails.version,
        type: previousValue.type || sourceState.policyDetails.type
      };

      return deepEqual(nextValue, previousValue) ? previousValue : nextValue;
    });

    setInitialEditorState((previousValue) => {
      if (!previousValue) {
        return previousValue;
      }

      const nextValue = {
        ...previousValue,
        policyDetails: {
          policyName:
            previousValue.policyDetails.policyName ||
            sourceState.policyDetails.policyName,
          version:
            previousValue.policyDetails.version ||
            sourceState.policyDetails.version,
          type:
            previousValue.policyDetails.type || sourceState.policyDetails.type
        }
      };

      return deepEqual(nextValue, previousValue) ? previousValue : nextValue;
    });
  }, [editorInitialized, existingPolicy, isDirty, sourceState]);

  const handlePolicyDetailsChange = (
    field: keyof PolicyDetailsState,
    value: string
  ) => {
    setPolicyDetails((previousValue) => ({
      ...previousValue,
      [field]: value
    }));
  };

  const updatePolicyCandidate = (
    index: number,
    updater: (currentValue: PolicyCandidateItem) => PolicyCandidateItem,
    options?: {
      markEdited?: boolean;
    }
  ) => {
    setPolicyCategories((previousValue) =>
      previousValue.map((item, itemIndex) =>
        itemIndex === index
          ? (() => {
              const nextValue = updater(item);

              if (deepEqual(item, nextValue)) {
                return item;
              }

              if (options?.markEdited === false) {
                return nextValue;
              }

              return withEditedPolicyCandidate(
                nextValue,
                user.sub || user.username || '',
                user.name || ''
              );
            })()
          : item
      )
    );
  };

  const handleRemoveCandidate = (index: number) => {
    setPolicyCategories((previousValue) =>
      previousValue.filter((_, itemIndex) => itemIndex !== index)
    );
  };

  const handleUndoCandidate = (index: number) => {
    const snapshotCandidate = initialEditorState?.policyCategories[index];

    if (!snapshotCandidate) {
      return;
    }

    updatePolicyCandidate(index, () => cloneValue(snapshotCandidate), {
      markEdited: false
    });
  };

  const handleInteractiveCardKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
    onActivate: () => void
  ) => {
    if (event.target !== event.currentTarget) {
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    onActivate();
  };

  const resetHighlightSelectionState = useCallback(() => {
    setActiveCandidateCardKey(null);
    setActiveHighlightAllSelectionCardKey(null);
    exitHighlightAllMode();
  }, [exitHighlightAllMode]);

  const clearViewerAnnotations = useCallback(
    async (showError = true, trackLoading = true) => {
      if (!viewerRef?.current?.clearAnnotations) {
        if (showError) {
          setAlert(
            'PDF annotations are not ready to be cleared yet.',
            'error',
            4
          );
        }
        return;
      }

      if (trackLoading) {
        setClearAnnotationsLoading(true);
      }

      try {
        await viewerRef.current.clearAnnotations('programmatic');
        setHighlightAllAppliedPageSignatures({});
        highlightAllAppliedPageSignaturesRef.current = {};
      } catch (clearAnnotationsError) {
        if (showError) {
          setAlert(getErrorMessage(clearAnnotationsError), 'error', 4);
        }
      } finally {
        if (trackLoading) {
          setClearAnnotationsLoading(false);
        }
      }
    },
    [setAlert, viewerRef]
  );

  const buildPageScopedAnnotationRequest = useCallback(
    (
      request: PdfViewerAnnotationRequest,
      pageNumber: number
    ): PdfViewerAnnotationRequest => ({
      ...request,
      pageNumber,
      pageNumbers: [pageNumber],
      reference: request.reference
        ? {
            ...request.reference,
            pageNumbers: [pageNumber]
          }
        : request.reference,
      scrollToPage: false
    }),
    []
  );

  const removeMatchingAnnotationRequest = useCallback(
    async (
      request: PdfViewerAnnotationRequest,
      annotationStyle: PdfViewerAnnotationStyle = 'default',
      pageNumber?: number
    ) => {
      const removeMatchingAnnotations =
        viewerRef?.current?.removeMatchingAnnotations;

      if (!removeMatchingAnnotations) {
        throw new Error('PDF annotations are not ready yet.');
      }

      const scopedRequest =
        typeof pageNumber === 'number'
          ? buildPageScopedAnnotationRequest(request, pageNumber)
          : {
              ...request,
              scrollToPage: false
            };

      await removeMatchingAnnotations({
        ...scopedRequest,
        annotationStyle,
        scrollToPage: false
      });
    },
    [buildPageScopedAnnotationRequest, viewerRef]
  );

  const highlightSingleItem = useCallback(
    async (
      cardKey: string,
      request: PdfViewerAnnotationRequest,
      isActive: boolean
    ) => {
      const activeHighlightAllSelectionRequest =
        activeHighlightAllSelectionRequestRef.current;
      const isCurrentHighlightAllSelection =
        activeHighlightAllSelectionCardKey === cardKey;

      if (isActive && !showAllItemsActive) {
        await clearViewerAnnotations(false);
        resetHighlightSelectionState();
        return;
      }

      if (
        !viewerRef?.current?.annotateMatches ||
        (showAllItemsActive && !viewerRef?.current?.removeMatchingAnnotations)
      ) {
        setAlert('PDF annotations are not ready yet.', 'error', 4);
        return;
      }

      if (highlightSelectionActionInFlightRef.current) {
        return;
      }

      highlightSelectionActionInFlightRef.current = true;

      try {
        if (showAllItemsActive) {
          if (
            isCurrentHighlightAllSelection &&
            activeHighlightAllSelectionRequest
          ) {
            await removeMatchingAnnotationRequest(
              activeHighlightAllSelectionRequest,
              'active-selection'
            );
            setActiveHighlightAllSelectionCardKey(null);
            activeHighlightAllSelectionRequestRef.current = null;
            return;
          }

          if (isCurrentHighlightAllSelection) {
            setActiveHighlightAllSelectionCardKey(null);
            activeHighlightAllSelectionRequestRef.current = null;
            return;
          }

          if (activeHighlightAllSelectionRequest) {
            await removeMatchingAnnotationRequest(
              activeHighlightAllSelectionRequest,
              'active-selection'
            );
            activeHighlightAllSelectionRequestRef.current = null;
            setActiveHighlightAllSelectionCardKey(null);
          }

          await viewerRef.current.annotateMatches({
            ...request,
            annotationStyle: 'active-selection'
          });
          activeHighlightAllSelectionRequestRef.current = {
            ...request,
            pageNumbers: request.pageNumbers
              ? [...request.pageNumbers]
              : undefined,
            query: Array.isArray(request.query)
              ? [...request.query]
              : request.query,
            reference: request.reference
              ? {
                  ...request.reference,
                  contextIds: request.reference.contextIds
                    ? [...request.reference.contextIds]
                    : undefined,
                  pageNumbers: request.reference.pageNumbers
                    ? [...request.reference.pageNumbers]
                    : undefined,
                  sentenceIds: request.reference.sentenceIds
                    ? [...request.reference.sentenceIds]
                    : undefined
                }
              : undefined
          };
          setActiveHighlightAllSelectionCardKey(cardKey);
          setActiveCandidateCardKey(null);
          return;
        }

        resetHighlightSelectionState();
        setActiveCandidateCardKey(cardKey);
        await clearViewerAnnotations(false);
        await viewerRef.current.annotateMatches({
          ...request
        });
      } catch (highlightError) {
        if (!showAllItemsActive) {
          setActiveCandidateCardKey(null);
        }
        setAlert(getErrorMessage(highlightError), 'error', 4);
      } finally {
        highlightSelectionActionInFlightRef.current = false;
      }
    },
    [
      activeHighlightAllSelectionCardKey,
      clearViewerAnnotations,
      removeMatchingAnnotationRequest,
      resetHighlightSelectionState,
      setAlert,
      showAllItemsActive,
      viewerRef
    ]
  );

  const handleDimensionFilterChange = useCallback(
    async (nextOption: OptionDefinition | null) => {
      setSelectedDimensionFilter(nextOption);
      resetHighlightSelectionState();
      await clearViewerAnnotations(false, false);
    },
    [clearViewerAnnotations, resetHighlightSelectionState]
  );

  const handleBenchmarkSourceFilterChange = useCallback(
    async (nextOption: OptionDefinition | null) => {
      setSelectedBenchmarkSourceFilter(nextOption);
      resetHighlightSelectionState();
      await clearViewerAnnotations(false, false);
    },
    [clearViewerAnnotations, resetHighlightSelectionState]
  );

  const handleCurrentPageFilterToggle = useCallback(async () => {
    if (!hasCurrentPdfPage && !filterByCurrentPage) {
      return;
    }

    setFilterByCurrentPage((previousValue) => !previousValue);
    // resetHighlightSelectionState();
    // await clearViewerAnnotations(false, false);
  }, [
    clearViewerAnnotations,
    filterByCurrentPage,
    hasCurrentPdfPage,
    resetHighlightSelectionState
  ]);

  const handleClearCandidateFilters = useCallback(async () => {
    setSelectedDimensionFilter(null);
    setSelectedBenchmarkSourceFilter(null);
    setFilterByCurrentPage(false);
    resetHighlightSelectionState();
    await clearViewerAnnotations(false, false);
  }, [clearViewerAnnotations, resetHighlightSelectionState]);

  const reportHighlightAllError = useCallback(
    (error: unknown) => {
      if (isSiblingTaskAbortError(error)) {
        console.warn(
          '[PDF annotations] ignoring aborted highlight-all task',
          error
        );
        return;
      }

      setAlert(getErrorMessage(error), 'error', 4);
    },
    [setAlert]
  );

  const handleCancelManualBenchmarkDraft = () => {
    if (activeCandidateCardKey === MANUAL_BENCHMARK_CARD_KEY) {
      resetHighlightSelectionState();
    }

    setManualBenchmarkDraft(null);
    setManualBenchmarkError('');
  };

  const handleCreateBlankManualBenchmarkDraft = useCallback(async () => {
    const nextSelectionKey =
      lastProcessedManualBenchmarkSelectionKeyRef.current + 1;

    lastProcessedManualBenchmarkSelectionKeyRef.current = nextSelectionKey;
    setManualBenchmarkDraft({
      pageNumbers:
        normalizedCurrentPdfPage !== null ? [normalizedCurrentPdfPage] : [],
      sourceText: '',
      selectionKey: nextSelectionKey
    });
    setManualBenchmarkError('');
    exitHighlightAllMode();
    await clearViewerAnnotations(false, false);
    setActiveCandidateCardKey(MANUAL_BENCHMARK_CARD_KEY);
  }, [clearViewerAnnotations, exitHighlightAllMode, normalizedCurrentPdfPage]);

  const handleManualBenchmarkDraftSourceTextChange = useCallback(
    (value: string) => {
      setManualBenchmarkDraft((previousValue) =>
        previousValue
          ? {
              ...previousValue,
              sourceText: value
            }
          : previousValue
      );
      setManualBenchmarkError('');
    },
    []
  );

  const syncPreparedPageAnnotationBatchesFromViewer = useCallback(
    (pageNumbers?: number[]) => {
      const exportedBatches =
        viewerRef?.current?.exportPreparedAnnotationBatches?.();

      if (!exportedBatches) {
        return;
      }

      const nextPageBatches =
        buildSavedPreparedPageAnnotationBatchesFromCacheMap(
          exportedBatches,
          highlightAllBatchSignatureByPage,
          pageNumbers
        );

      setSavedPreparedPageAnnotationBatches((previousValue) => {
        if (!pageNumbers || pageNumbers.length === 0) {
          return deepEqual(previousValue, nextPageBatches)
            ? previousValue
            : nextPageBatches;
        }

        const pageKeySet = new Set(
          pageNumbers.map((pageNumber) => String(pageNumber))
        );
        const nextValue = Object.fromEntries(
          Object.entries(previousValue).filter(
            ([pageKey]) => !pageKeySet.has(pageKey)
          )
        ) as SavedPreparedPageAnnotationBatches;

        Object.assign(nextValue, nextPageBatches);

        return deepEqual(previousValue, nextValue) ? previousValue : nextValue;
      });
    },
    [highlightAllBatchSignatureByPage, viewerRef]
  );

  const invalidatePreparedPageAnnotationBatch = useCallback(
    (pageNumber: number) => {
      const pageKey = String(pageNumber);
      const nextPreparedPageSignatures = {
        ...highlightAllPreparedPageSignaturesRef.current
      };

      delete nextPreparedPageSignatures[pageNumber];
      highlightAllPreparedPageSignaturesRef.current =
        nextPreparedPageSignatures;
      setHighlightAllPreparedPageSignatures((previousValue) =>
        previousValue[pageNumber] === undefined
          ? previousValue
          : nextPreparedPageSignatures
      );
      setSavedPreparedPageAnnotationBatches((previousValue) => {
        if (!previousValue[pageKey]) {
          return previousValue;
        }

        const nextValue = { ...previousValue };

        delete nextValue[pageKey];
        return nextValue;
      });
    },
    []
  );

  const ensureHighlightAllPagePrepared = useCallback(
    async (pageNumber: number) => {
      const prepareAnnotationBatch = viewerRef?.current?.prepareAnnotationBatch;
      const pageRequests = getHighlightAllRequestsForPage(pageNumber);
      const pageSignature = getHighlightAllBatchSignatureForPage(pageNumber);
      const preparationGeneration =
        highlightAllPreparationGenerationRef.current;

      if (!prepareAnnotationBatch) {
        throw new Error('PDF annotations are not ready yet.');
      }

      if (pageRequests.length === 0) {
        return;
      }

      if (
        highlightAllPreparedPageSignaturesRef.current[pageNumber] ===
        pageSignature
      ) {
        return;
      }

      const inFlightPreparation =
        highlightAllPagePreparationPromisesRef.current.get(pageNumber);

      if (
        inFlightPreparation &&
        inFlightPreparation.generation === preparationGeneration &&
        inFlightPreparation.signature === pageSignature
      ) {
        await inFlightPreparation.promise;
        return;
      }

      const nextPreparationPromise = (async () => {
        await prepareAnnotationBatch(
          getHighlightAllPageCacheKey(pageNumber),
          pageRequests
        );

        if (
          highlightAllPreparationGenerationRef.current !== preparationGeneration
        ) {
          return;
        }

        syncPreparedPageAnnotationBatchesFromViewer([pageNumber]);

        setHighlightAllPreparedPageSignatures((previousValue) => {
          if (previousValue[pageNumber] === pageSignature) {
            return previousValue;
          }

          const nextValue = {
            ...previousValue,
            [pageNumber]: pageSignature
          };

          highlightAllPreparedPageSignaturesRef.current = nextValue;
          return nextValue;
        });
      })();

      highlightAllPagePreparationPromisesRef.current.set(pageNumber, {
        generation: preparationGeneration,
        promise: nextPreparationPromise,
        signature: pageSignature
      });

      try {
        await nextPreparationPromise;
      } finally {
        const currentPreparation =
          highlightAllPagePreparationPromisesRef.current.get(pageNumber);

        if (currentPreparation?.promise === nextPreparationPromise) {
          highlightAllPagePreparationPromisesRef.current.delete(pageNumber);
        }
      }
    },
    [
      getHighlightAllBatchSignatureForPage,
      getHighlightAllRequestsForPage,
      syncPreparedPageAnnotationBatchesFromViewer,
      viewerRef
    ]
  );

  const showHighlightAllPage = useCallback(
    async (pageNumber: number) => {
      const applyPreparedAnnotationBatch =
        viewerRef?.current?.applyPreparedAnnotationBatch;
      const cacheKey = getHighlightAllPageCacheKey(pageNumber);
      const pageRequests = getHighlightAllRequestsForPage(pageNumber);
      const pageSignature = getHighlightAllBatchSignatureForPage(pageNumber);

      if (!applyPreparedAnnotationBatch) {
        throw new Error('PDF annotations are not ready yet.');
      }

      if (
        highlightAllAppliedPageSignaturesRef.current[pageNumber] ===
        pageSignature
      ) {
        return;
      }

      if (pageRequests.length === 0) {
        return;
      }

      const nextVersion = highlightAllPageChangeVersionRef.current + 1;

      highlightAllPageChangeVersionRef.current = nextVersion;

      await ensureHighlightAllPagePrepared(pageNumber);

      if (highlightAllPageChangeVersionRef.current !== nextVersion) {
        return;
      }

      try {
        await applyPreparedAnnotationBatch(cacheKey, {
          pageNumber,
          scrollToPage: false
        });
      } catch (applyPreparedAnnotationError) {
        if (
          !isMissingPreparedAnnotationBatchError(applyPreparedAnnotationError)
        ) {
          throw applyPreparedAnnotationError;
        }

        invalidatePreparedPageAnnotationBatch(pageNumber);
        await ensureHighlightAllPagePrepared(pageNumber);

        if (highlightAllPageChangeVersionRef.current !== nextVersion) {
          return;
        }

        await applyPreparedAnnotationBatch(cacheKey, {
          pageNumber,
          scrollToPage: false
        });
      }

      if (highlightAllPageChangeVersionRef.current === nextVersion) {
        setHighlightAllAppliedPageSignatures((previousValue) => {
          const nextValue = {
            ...previousValue,
            [pageNumber]: pageSignature
          };

          highlightAllAppliedPageSignaturesRef.current = nextValue;
          return nextValue;
        });
      }
    },
    [
      ensureHighlightAllPagePrepared,
      getHighlightAllBatchSignatureForPage,
      getHighlightAllRequestsForPage,
      invalidatePreparedPageAnnotationBatch,
      viewerRef
    ]
  );

  const toggleShowAllItems = async () => {
    if (showAllItemsActive) {
      resetHighlightSelectionState();
      await clearViewerAnnotations(false);
      return;
    }

    if (
      !viewerRef?.current?.prepareAnnotationBatch ||
      !viewerRef?.current?.applyPreparedAnnotationBatch
    ) {
      setAlert('PDF annotations are not ready yet.', 'error', 4);
      return;
    }

    if (highlightAllAnnotationRequests.length === 0) {
      setAlert('No policy candidates with source text were found.', 'error', 4);
      return;
    }

    resetHighlightSelectionState();
    setHighlightAllLoading(true);

    try {
      viewerRef.current.hydratePreparedAnnotationBatches?.(
        buildPreparedAnnotationBatchCacheMap(savedPreparedPageAnnotationBatches)
      );
      await clearViewerAnnotations(false);
      setShowAllItemsActive(true);
      onPreserveHighlightsOnPageChangeChange?.(true);
      onSelectSentenceIds?.(allCandidateSourceSentenceIds);
      setAlert(
        'Highlight all mode enabled. Pages will highlight as you scroll.',
        'success',
        4
      );
    } catch (highlightAllError) {
      setAlert(getErrorMessage(highlightAllError), 'error', 4);
    } finally {
      setHighlightAllLoading(false);
    }
  };

  useEffect(() => {
    if (!showAllItemsActive) {
      return;
    }

    const availablePageNumbers = Array.from(
      highlightAllAnnotationRequestsByPage.keys()
    ).sort(
      (leftPageNumber, rightPageNumber) => leftPageNumber - rightPageNumber
    );
    const targetPageNumber =
      currentPdfPage === null || currentPdfPage === undefined
        ? (availablePageNumbers[0] ?? null)
        : highlightAllAnnotationRequestsByPage.has(currentPdfPage)
          ? currentPdfPage
          : null;

    if (!targetPageNumber) {
      return;
    }

    void showHighlightAllPage(targetPageNumber).catch((highlightAllError) => {
      reportHighlightAllError(highlightAllError);
    });
  }, [
    currentPdfPage,
    highlightAllAnnotationRequestsByPage,
    reportHighlightAllError,
    showAllItemsActive,
    showHighlightAllPage
  ]);

  useEffect(() => {
    if (!showAllItemsActive) {
      return;
    }

    const availablePageNumbers = Array.from(
      highlightAllAnnotationRequestsByPage.keys()
    ).sort(
      (leftPageNumber, rightPageNumber) => leftPageNumber - rightPageNumber
    );
    const targetPageNumber =
      currentPdfPage === null || currentPdfPage === undefined
        ? (availablePageNumbers[0] ?? null)
        : highlightAllAnnotationRequestsByPage.has(currentPdfPage)
          ? currentPdfPage
          : null;

    if (!targetPageNumber) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      const targetSignature =
        getHighlightAllBatchSignatureForPage(targetPageNumber);

      if (
        highlightAllAppliedPageSignaturesRef.current[targetPageNumber] ===
        targetSignature
      ) {
        return;
      }

      void showHighlightAllPage(targetPageNumber).catch((highlightAllError) => {
        reportHighlightAllError(highlightAllError);
      });
    }, 150);

    return () => window.clearTimeout(timeoutId);
  }, [
    currentPdfPage,
    getHighlightAllBatchSignatureForPage,
    highlightAllAnnotationRequestsByPage,
    reportHighlightAllError,
    showAllItemsActive,
    showHighlightAllPage
  ]);

  const handleCreateManualBenchmark = async () => {
    if (!documentId) {
      setAlert(
        'Missing document id. Unable to create benchmark rule.',
        'error',
        4
      );
      return;
    }

    const sourceText = manualBenchmarkDraft?.sourceText.trim();

    if (!sourceText) {
      setAlert(
        'Select source text before creating a benchmark rule.',
        'error',
        4
      );
      return;
    }

    const requestBody: ManualBenchmarkRequestBody = {
      documentId,
      pageNumber: manualBenchmarkDraft?.pageNumbers[0] ?? undefined,
      pageNumbers: manualBenchmarkDraft?.pageNumbers.length
        ? manualBenchmarkDraft.pageNumbers
        : undefined,
      parseType:
        parseTypeFromQuery ||
        policyDetails.type.trim() ||
        currentDocument?.documentType,
      requestId,
      sourceText
    };

    setManualBenchmarkLoading(true);
    setManualBenchmarkError('');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_KORU_API}/manualBenchmark`,
        requestBody,
        auth
      );
      const createdCandidates = extractPolicyCategories(response.data).map(
        (candidate) =>
          withUserCreatedPolicyCandidate(
            withPolicyCandidateSourcePageNumbers(
              candidate,
              manualBenchmarkDraft?.pageNumbers ?? []
            )
          )
      );

      if (createdCandidates.length === 0) {
        throw new Error(
          'The manual benchmark response did not include a candidate.'
        );
      }

      exitHighlightAllMode(false);
      setPolicyCategories((previousValue) =>
        mergePolicyCandidates(previousValue, createdCandidates)
      );
      setManualBenchmarkDraft(null);
      setAlert('Benchmark rule created.', 'success', 4);

      const nextActiveCardKey = getPolicyCandidateCardKey(
        createdCandidates[0],
        0
      );
      setActiveCandidateCardKey(nextActiveCardKey);

      const nextSentenceIds =
        createdCandidates[0].normalized_rule.source.sentenceIds;
      if (nextSentenceIds.length > 0) {
        onSelectSentenceIds?.(nextSentenceIds);
      }

      if (nextActiveCardKey) {
        window.requestAnimationFrame(() => {
          scrollCandidateCardIntoView(nextActiveCardKey);
        });
      }
    } catch (manualBenchmarkRequestError) {
      const errorMessage = getErrorMessage(manualBenchmarkRequestError);
      setManualBenchmarkError(errorMessage);
      setAlert(errorMessage, 'error', 4);
    } finally {
      setManualBenchmarkLoading(false);
    }
  };

  const renderExpectedValueNode = (
    candidateIndex: number,
    path: string,
    value: PolicyValue,
    templateVariablePaths: Set<string>
  ): ReactNode => {
    if (!pathReferencesTemplate(path, templateVariablePaths)) {
      return null;
    }

    const isNodeDirty = isExpectedValuePathDirty(candidateIndex, path, value);

    if (Array.isArray(value)) {
      const primitiveEntries = value.filter(
        (entry) => !Array.isArray(entry) && !isRecord(entry)
      );
      const isPrimitiveList = primitiveEntries.length === value.length;

      if (isPrimitiveList) {
        const visibleEntries = value
          .map((entry, entryIndex) => ({
            entry,
            entryIndex,
            path: `${path}.${entryIndex}`
          }))
          .filter(({ path: entryPath }) =>
            pathReferencesTemplate(entryPath, templateVariablePaths)
          );

        if (visibleEntries.length === 0) {
          return null;
        }

        return (
          <FormField
            key={path}
            label={formatVariableLabel(path)}
            className={getDirtyFieldContainerClassName(isNodeDirty)}
          >
            <SpaceBetween size="s">
              {visibleEntries.map(({ entry, entryIndex, path: entryPath }) => {
                const isEntryDirty = isExpectedValuePathDirty(
                  candidateIndex,
                  entryPath,
                  entry
                );

                return (
                  <div
                    key={`${path}-${entryIndex}`}
                    className={classNames(
                      'flex items-center gap-2 rounded-xl',
                      isEntryDirty
                        ? 'border border-amber-200 bg-amber-50/60 px-3 py-2.5'
                        : ''
                    )}
                  >
                    <Input
                      className={getDirtyInputClassName(isEntryDirty)}
                      onClick={stopEventPropagation}
                      onKeyDown={stopEventPropagation}
                      value={formatPrimitiveValue(entry)}
                      onChange={({ detail }) =>
                        updatePolicyCandidate(
                          candidateIndex,
                          (currentValue) => ({
                            ...currentValue,
                            normalized_rule: {
                              ...currentValue.normalized_rule,
                              expected_value_structure: setNestedPolicyValue(
                                currentValue.normalized_rule
                                  .expected_value_structure,
                                entryPath,
                                coercePrimitiveValue(detail.value, entry)
                              )
                            }
                          })
                        )
                      }
                    />
                    <button
                      type="button"
                      aria-label={`Remove ${formatVariableLabel(path)} item ${
                        entryIndex + 1
                      }`}
                      className="inline-flex h-8 w-8 items-center justify-center rounded border border-gray-300 text-gray-700 transition hover:bg-gray-100"
                      onClick={() =>
                        updatePolicyCandidate(
                          candidateIndex,
                          (currentValue) => ({
                            ...currentValue,
                            normalized_rule: {
                              ...currentValue.normalized_rule,
                              expected_value_structure:
                                removeNestedPolicyArrayItem(
                                  currentValue.normalized_rule
                                    .expected_value_structure,
                                  entryPath
                                )
                            }
                          })
                        )
                      }
                    >
                      <X size={16} />
                    </button>
                  </div>
                );
              })}
            </SpaceBetween>
          </FormField>
        );
      }

      const childNodes = value
        .map((entry, entryIndex) =>
          renderExpectedValueNode(
            candidateIndex,
            `${path}.${entryIndex}`,
            entry,
            templateVariablePaths
          )
        )
        .filter(Boolean);

      if (childNodes.length === 0) {
        return null;
      }

      return (
        <FormField
          key={path}
          label={formatVariableLabel(path)}
          className={getDirtyFieldContainerClassName(isNodeDirty)}
        >
          <SpaceBetween size="s">
            {childNodes.map((childNode, childIndex) => (
              <Box
                key={`${path}-${childIndex}`}
                padding="m"
                className={classNames(
                  'rounded border border-gray-200 bg-gray-50',
                  isNodeDirty ? 'border-amber-200 bg-amber-50/40' : ''
                )}
              >
                {childNode}
              </Box>
            ))}
          </SpaceBetween>
        </FormField>
      );
    }

    if (isRecord(value)) {
      const childNodes = Object.entries(value)
        .map(([childKey, childValue]) =>
          renderExpectedValueNode(
            candidateIndex,
            `${path}.${childKey}`,
            childValue as PolicyValue,
            templateVariablePaths
          )
        )
        .filter(Boolean);

      if (childNodes.length === 0) {
        return null;
      }

      return (
        <FormField
          key={path}
          label={formatVariableLabel(path)}
          className={getDirtyFieldContainerClassName(isNodeDirty)}
        >
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {childNodes}
          </div>
        </FormField>
      );
    }

    return (
      <FormField
        key={path}
        label={formatVariableLabel(path)}
        className={getDirtyFieldContainerClassName(isNodeDirty)}
      >
        <Input
          className={getDirtyInputClassName(isNodeDirty)}
          onClick={stopEventPropagation}
          onKeyDown={stopEventPropagation}
          value={formatPrimitiveValue(value)}
          onChange={({ detail }) =>
            updatePolicyCandidate(candidateIndex, (currentValue) => ({
              ...currentValue,
              normalized_rule: {
                ...currentValue.normalized_rule,
                expected_value_structure: setNestedPolicyValue(
                  currentValue.normalized_rule.expected_value_structure,
                  path,
                  coercePrimitiveValue(detail.value, value)
                )
              }
            }))
          }
        />
      </FormField>
    );
  };

  const handleReset = () => {
    if (!initialEditorState) {
      return;
    }

    const nextState = processPolicyEditorStateOnce(
      cloneValue(initialEditorState)
    );
    setPolicyDetails(nextState.policyDetails);
    setPolicyCategories(nextState.policyCategories);
    setPolicyViewerProcessed(nextState.policyViewerProcessed);
  };

  // const handleSave = async () => {
  //   if (!documentId) {
  //     setAlert('Missing document id. Unable to save policy.', 'error', 4);
  //     return;
  //   }
  //   const payload = cloneValue(policyCategories) as unknown as Partial<PolicyItem>;

  //   const result =
  //     existingPolicy?._id !== undefined
  //       ? await updatePolicy(existingPolicy._id, payload)
  //       : await addPolicy(payload);

  //   if (axios.isAxiosError(result) || result instanceof Error) {
  //     return;
  //   }
  //   setAlert(
  //     existingPolicy?._id ? 'Policy updated.' : 'Policy saved.',
  //     'success',
  //     4
  //   );
  // };

  const savePolicy = async () => {
    if (!documentId) {
      setAlert('Missing document id. Unable to save policy.', 'error', 4);
      return;
    }

    const isNewPolicy = !existingPolicy?._id;
    const policyName = policyDetails.policyName.trim();
    const typedVersion = toFiniteNumber(policyDetails.version.trim());
    const existingVersion = toFiniteNumber(existingPolicy?.version);
    const nextVersion = existingPolicy?._id
      ? (existingVersion ?? typedVersion ?? 0) + 1
      : (typedVersion ?? 1);
    const policyRecordPayload = {
      customerId: currentDocument?.customerId || user.customerId,
      documentId,
      policyName,
      version: nextVersion,
      type: policyDetails.type.trim()
    };
    const policyBody = cloneValue(policyCategories).map((item) => ({
      ...item,
      normalized_rule: {
        ...item.normalized_rule,
        normalized_rule_statement: renderNormalizedRuleTemplate(
          item.normalized_rule.normalized_rule_statement_template,
          item.normalized_rule.expected_value_structure
        )
      }
    }));
    const exportedPreparedAnnotationBatches =
      viewerRef?.current?.exportPreparedAnnotationBatches?.();
    const savedPreparedPageAnnotations = {
      ...filterSavedPreparedPageAnnotationBatches(
        savedPreparedPageAnnotationBatches,
        highlightAllBatchSignatureByPage
      ),
      ...(exportedPreparedAnnotationBatches
        ? buildSavedPreparedPageAnnotationBatchesFromCacheMap(
            exportedPreparedAnnotationBatches,
            highlightAllBatchSignatureByPage
          )
        : {})
    };
    console.log('policy body before upload', policyBody);

    setCreatePolicyLoading(true);

    try {
      const policyResult = existingPolicy?._id
        ? await updatePolicy(existingPolicy._id, policyRecordPayload)
        : await addPolicy(policyRecordPayload);

      if (axios.isAxiosError(policyResult) || policyResult instanceof Error) {
        throw policyResult;
      }

      const policyDocumentId =
        typeof policyResult?.documentId === 'string'
          ? policyResult.documentId
          : '';

      if (!policyDocumentId) {
        throw new Error('Policy save did not return a policy ID.');
      }

      const presignedUrl = await axios.post(
        `${process.env.REACT_APP_KORU_API}/get-presigned-url?type=savePolicyJson`,
        { documentId: policyDocumentId },
        auth
      );
      const uploadUrl = presignedUrl?.data?.url;
      if (!uploadUrl) {
        throw new Error('No presigned URL found for policy save.');
      }
      if (uploadUrl === 'locked') {
        throw new Error('File locked by another user.');
      }

      const savedPolicyDetails = {
        policyName,
        version: String(nextVersion),
        type: policyDetails.type.trim()
      };
      const jsonPayload = JSON.stringify({
        [POLICY_VIEWER_PROCESSED_FLAG]: true,
        benchmark_4b_result: policyBody,
        benchmark_4b_results: {
          policyDetails: savedPolicyDetails
        },
        policyDetails: savedPolicyDetails,
        pageItems: cloneValue(fallbackPageItems),
        preparedPageAnnotations: savedPreparedPageAnnotations
      });
      const blob = new Blob([jsonPayload], { type: 'application/json' });
      await axios.put(uploadUrl, blob, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // if (isNewPolicy && policyDocumentId !== documentId) {
      //   const deletePresignedUrl = await axios.post(
      //     `${process.env.REACT_APP_KORU_API}/get-presigned-url?type=deletePolicyJson`,
      //     { documentId },
      //     auth
      //   );
      //   const deleteUrl = deletePresignedUrl?.data?.url;

      //   if (!deleteUrl) {
      //     throw new Error('No presigned URL found for policy JSON delete.');
      //   }
      //   if (deleteUrl === 'locked') {
      //     throw new Error('Policy JSON delete is locked by another user.');
      //   }

      //   await axios.delete(deleteUrl);
      // }

      await getPolicies();
      const savedPolicyCategories = cloneValue(policyBody);
      const savedEditorState = {
        policyCategories: cloneValue(savedPolicyCategories),
        policyDetails: savedPolicyDetails,
        policyViewerProcessed: true
      };

      setPolicyDetails(savedPolicyDetails);
      setPolicyCategories(savedPolicyCategories);
      setPolicyViewerProcessed(true);
      setSavedPreparedPageAnnotationBatches(savedPreparedPageAnnotations);
      setSourceState(cloneValue(savedEditorState));
      setInitialEditorState(cloneValue(savedEditorState));
      setAlert(
        existingPolicy?._id ? 'Policy updated.' : 'Policy created.',
        'success',
        4
      );
    } catch (saveError) {
      setAlert(getErrorMessage(saveError), 'error', 4);
    } finally {
      setCreatePolicyLoading(false);
    }
  };

  const headerContent = (
    <Header
      variant="h1"
      description={
        currentDocument?.documentFileName || documentId || 'Policy viewer'
      }
      actions={
        <div className="flex max-w-full flex-wrap justify-end gap-2">
          {isDirty ? (
            <div className="inline-flex h-9 items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 text-sm font-semibold text-amber-900 shadow-sm">
              <span className="h-2 w-2 rounded-full bg-amber-500" />
              Unsaved changes
            </div>
          ) : null}
          {typeof showPdf === 'boolean' && onToggleShowPdf ? (
            <Button onClick={onToggleShowPdf}>
              {showPdf ? 'Hide PDF' : 'Show PDF'}
            </Button>
          ) : null}
          {isPdfVisible ? (
            <Button
              onClick={toggleShowAllItems}
              disabled={
                filteredPolicyCandidateEntries.length === 0 ||
                clearAnnotationsLoading ||
                highlightAllLoading
              }
              loading={highlightAllLoading || clearAnnotationsLoading}
              variant={showAllItemsActive ? 'primary' : 'normal'}
              aria-label={
                showAllItemsActive
                  ? 'Turn highlight all off'
                  : 'Turn highlight all on'
              }
            >
              {showAllItemsActive ? 'Highlights On' : 'Highlights Off'}
            </Button>
          ) : null}
          {existingPolicy?._id && isDirty ? (
            <Button
              variant="primary"
              onClick={savePolicy}
              loading={createPolicyLoading}
              disabled={!documentId || saveLoading}
            >
              Update Policy
            </Button>
          ) : null}
          {!existingPolicy?._id ? (
            <Button
              variant="primary"
              onClick={savePolicy}
              loading={createPolicyLoading}
              disabled={!documentId || saveLoading}
            >
              Create Policy
            </Button>
          ) : null}
          <div className="relative" ref={headerActionsMenuRef}>
            <button
              type="button"
              aria-label="Show more actions"
              aria-expanded={isHeaderActionsMenuOpen}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-2"
              style={
                {
                  '--tw-ring-color': `${primaryButtonGreen}66`
                } as CSSProperties
              }
              onClick={() =>
                setIsHeaderActionsMenuOpen((currentValue) => !currentValue)
              }
            >
              <MoreVertical size={18} />
            </button>
            {isHeaderActionsMenuOpen ? (
              <div className="absolute right-0 top-full z-30 mt-2 w-56 overflow-hidden rounded-xl border border-slate-200 bg-white p-1 shadow-xl ring-1 ring-black/5">
                <button
                  type="button"
                  disabled={!documentId || manualBenchmarkLoading}
                  className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400 disabled:hover:bg-transparent"
                  onClick={() => {
                    setIsHeaderActionsMenuOpen(false);
                    void handleCreateBlankManualBenchmarkDraft();
                  }}
                >
                  Create Rule
                </button>
                {isDirty ? (
                  <button
                    type="button"
                    disabled={saveLoading}
                    className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400 disabled:hover:bg-transparent"
                    onClick={() => {
                      setIsHeaderActionsMenuOpen(false);
                      handleReset();
                    }}
                  >
                    Reset
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      }
    >
      Policy Document Viewer
    </Header>
  );
  const viewerContent = (
    <div className="flex h-full min-h-0 flex-col gap-3 overflow-hidden">
      {!documentId ? (
        <Alert type="error">Missing `documentId` in the search params.</Alert>
      ) : null}

      {error ? <Alert type="error">{error}</Alert> : null}

      <div
        ref={candidateStickyControlsRef}
        className="sticky top-0 z-20 shrink-0 space-y-3 border-b border-slate-200 bg-white/95 pb-3 backdrop-blur"
      >
        <Header counter={`(${visibleCandidateCount})`}>
          Policy Candidates
        </Header>

        <div className="grid grid-cols-1 gap-3 rounded-xl border border-slate-200 bg-slate-50/90 p-3 md:grid-cols-[minmax(0,1.3fr)_minmax(0,1.1fr)_auto_auto] md:items-end">
          <FormField className="min-w-0 flex-1" label="Filter by Dimension">
            <Select
              selectedOption={selectedDimensionFilter}
              onChange={({ detail }) => {
                void handleDimensionFilterChange(detail.selectedOption);
              }}
              options={dimensionFilterOptions}
              placeholder="Select a dimension"
              disabled={loading || dimensionFilterOptions.length === 0}
            />
          </FormField>
          {benchmarkSourceFilterOptions.length > 0 ? (
            <FormField
              className="min-w-0 flex-1"
              label="Filter by Benchmark Source"
            >
              <Select
                selectedOption={selectedBenchmarkSourceFilter}
                onChange={({ detail }) => {
                  void handleBenchmarkSourceFilterChange(detail.selectedOption);
                }}
                options={benchmarkSourceFilterOptions}
                placeholder="Select a benchmark source"
                disabled={loading}
              />
            </FormField>
          ) : null}
          <FormField className="min-w-0" label="Filter by Current Page">
            <Button
              variant={filterByCurrentPage ? 'primary' : 'normal'}
              onClick={() => {
                void handleCurrentPageFilterToggle();
              }}
              disabled={!filterByCurrentPage && !hasCurrentPdfPage}
            >
              {filterByCurrentPage
                ? `Current Page ON${
                    normalizedCurrentPdfPage !== null
                      ? ` (${normalizedCurrentPdfPage})`
                      : ''
                  }`
                : normalizedCurrentPdfPage !== null
                  ? `Use Current Page (${normalizedCurrentPdfPage})`
                  : 'Current Page Unavailable'}
            </Button>
          </FormField>
          <Button
            onClick={() => {
              void handleClearCandidateFilters();
            }}
            disabled={
              !selectedDimensionFilter &&
              !selectedBenchmarkSourceFilter &&
              !filterByCurrentPage
            }
          >
            Clear Filter
          </Button>
        </div>
      </div>

      <div
        ref={candidateListContainerRef}
        className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto pr-1"
      >
        <div className="flex flex-col gap-4 pb-4">
          <Container header={<Header variant="h2">Policy Details</Header>}>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <FormField
                label="Policy Name"
                className={getDirtyFieldContainerClassName(
                  isPolicyDetailsFieldDirty('policyName')
                )}
              >
                <Input
                  className={getDirtyInputClassName(
                    isPolicyDetailsFieldDirty('policyName')
                  )}
                  value={policyDetails.policyName}
                  onChange={({ detail }) =>
                    handlePolicyDetailsChange('policyName', detail.value)
                  }
                />
              </FormField>

              <FormField
                label="Version"
                className={getDirtyFieldContainerClassName(
                  isPolicyDetailsFieldDirty('version')
                )}
              >
                <Input
                  className={getDirtyInputClassName(
                    isPolicyDetailsFieldDirty('version')
                  )}
                  value={policyDetails.version}
                  onChange={({ detail }) =>
                    handlePolicyDetailsChange('version', detail.value)
                  }
                />
              </FormField>

              <FormField
                label="Type"
                className={getDirtyFieldContainerClassName(
                  isPolicyDetailsFieldDirty('type')
                )}
              >
                <Input
                  className={getDirtyInputClassName(
                    isPolicyDetailsFieldDirty('type')
                  )}
                  value={policyDetails.type}
                  onChange={({ detail }) =>
                    handlePolicyDetailsChange('type', detail.value)
                  }
                />
              </FormField>
            </div>
          </Container>

          {!loading && removedCandidateCount > 0 ? (
            <Alert type="warning">
              {removedCandidateCount} candidate
              {removedCandidateCount === 1 ? '' : 's'} removed from the current
              state.
            </Alert>
          ) : null}

          {loading ? (
            <Box textAlign="center" padding={{ vertical: 'xl' }}>
              <Spinner size="large" />
            </Box>
          ) : null}

          {!loading && visibleCandidateCount === 0 ? (
            <Box margin={{ vertical: 'xs' }} textAlign="center">
              {emptyCandidateMessage}
            </Box>
          ) : null}

          {!loading ? (
            <SpaceBetween className="min-w-0" size="l">
              {groupedPolicyCategories.map((group) => (
                <section
                  key={group.dimensionKey}
                  className="mx-auto w-full max-w-[52rem] min-w-0"
                >
                  <SpaceBetween size="m">
                    <Header counter={`(${group.items.length})`} variant="h2">
                      {group.dimensionLabel}
                    </Header>

                    <div className="grid w-full min-w-0 grid-cols-1 gap-3">
                      {group.items.map(({ item, index }) => {
                        const cardKey = getPolicyCandidateCardKey(item, index);
                        const candidateLabel =
                          item.candidate_id.trim() ||
                          `Policy Candidate ${index + 1}`;
                        const initialCandidate =
                          initialEditorState?.policyCategories[index];
                        const isCandidateDirty =
                          initialCandidate === undefined ||
                          !deepEqual(initialCandidate, item);
                        const isActive = showAllItemsActive
                          ? activeHighlightAllSelectionCardKey === cardKey
                          : activeCandidateCardKey === cardKey;
                        const isEdited = item.edited === true;
                        const editedByName = item.editedByName?.trim() || '';
                        const templateVariablePaths = getTemplateVariablePaths(
                          item.normalized_rule
                            .normalized_rule_statement_template
                        );
                        const pageNumbers = getPolicyCandidateSourcePageNumbers(
                          item,
                          contextPageMap
                        );
                        const expectedValueNodes = Object.entries(
                          item.normalized_rule.expected_value_structure
                        )
                          .map(([fieldKey, fieldValue]) =>
                            renderExpectedValueNode(
                              index,
                              fieldKey,
                              fieldValue,
                              templateVariablePaths
                            )
                          )
                          .filter(Boolean);

                        const toggleCandidateHighlight = async () => {
                          const request: PdfViewerAnnotationRequest = {
                            pageNumber: pageNumbers[0],
                            pageNumbers,
                            query: item.normalized_rule.source.source_text!,
                            reference: buildCandidateAnnotationReference(
                              item,
                              index,
                              pageNumbers
                            )
                          };

                          await highlightSingleItem(cardKey, request, isActive);
                        };

                        return (
                          <div
                            key={`${cardKey}-${index}`}
                            ref={(node) => {
                              candidateCardRefs.current[cardKey] = node;
                            }}
                            role="button"
                            tabIndex={0}
                            aria-pressed={isActive}
                            className={`w-full min-w-0 rounded-2xl transition focus:outline-none focus:ring-2 focus:ring-sky-400 ${
                              isActive ? 'ring-1 ring-inset ring-sky-400' : ''
                            }`}
                            onClick={toggleCandidateHighlight}
                            onKeyDown={(event) =>
                              handleInteractiveCardKeyDown(
                                event,
                                toggleCandidateHighlight
                              )
                            }
                          >
                            <Container
                              className={classNames(
                                'transition-shadow transition-colors',
                                isActive
                                  ? 'border-sky-300 bg-sky-50/40 shadow-[0_0_0_1px_rgba(14,165,233,0.2),0_16px_36px_rgba(15,23,42,0.08)]'
                                  : 'hover:border-slate-300 hover:bg-slate-50/60 hover:shadow-md'
                              )}
                              header={
                                <Header
                                  variant="h3"
                                  actions={
                                    <SpaceBetween
                                      direction="horizontal"
                                      size="xs"
                                    >
                                      {isCandidateDirty ? (
                                        <button
                                          type="button"
                                          aria-label={`Save ${candidateLabel}`}
                                          title="Save policy"
                                          disabled={!documentId || saveLoading}
                                          className="inline-flex h-8 w-8 items-center justify-center rounded border border-gray-300 text-gray-700 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            savePolicy();
                                          }}
                                        >
                                          <Save size={16} color="green" />
                                        </button>
                                      ) : null}
                                      {isCandidateDirty && initialCandidate ? (
                                        <button
                                          type="button"
                                          aria-label={`Undo changes to ${candidateLabel}`}
                                          title="Undo changes"
                                          className="inline-flex h-8 w-8 items-center justify-center rounded border border-gray-300 text-gray-700 transition hover:bg-gray-100"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            handleUndoCandidate(index);
                                          }}
                                        >
                                          <RotateCcw size={16} />
                                        </button>
                                      ) : null}
                                      <button
                                        type="button"
                                        aria-label={`Remove ${candidateLabel}`}
                                        className="inline-flex h-8 w-8 items-center justify-center rounded border border-gray-300 text-gray-700 transition hover:bg-gray-100"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          handleRemoveCandidate(index);
                                        }}
                                      >
                                        <Trash2 size={16} color="red" />
                                      </button>
                                    </SpaceBetween>
                                  }
                                >
                                  <div className="flex min-w-0 flex-col gap-1">
                                    <span className="truncate">
                                      {candidateLabel}
                                    </span>
                                    {isEdited ? (
                                      <div className="flex flex-wrap items-center gap-2 text-xs font-normal text-amber-800">
                                        <span className="rounded-full bg-amber-100 px-2 py-0.5 font-medium uppercase tracking-[0.08em] text-amber-900">
                                          Edited
                                        </span>
                                        <span>
                                          Edited by{' '}
                                          {editedByName || 'Unknown user'}
                                        </span>
                                      </div>
                                    ) : null}
                                  </div>
                                </Header>
                              }
                            >
                              <SpaceBetween size="l">
                                <SpaceBetween size="s">
                                  <Header variant="h3">Editable Items</Header>

                                  {expectedValueNodes.length > 0 ? (
                                    <div
                                      className={classNames(
                                        'grid grid-cols-1',
                                        isCandidateDirty ? 'gap-5' : 'gap-4'
                                      )}
                                    >
                                      {expectedValueNodes}
                                    </div>
                                  ) : (
                                    <Box color="text-body-secondary">
                                      No Editable items.
                                    </Box>
                                  )}
                                </SpaceBetween>

                                <FormField label="Normalized Rule">
                                  <Box
                                    padding="m"
                                    className="rounded border border-gray-300 bg-gray-50 whitespace-pre-wrap"
                                  >
                                    {renderNormalizedRuleTemplate(
                                      item.normalized_rule
                                        .normalized_rule_statement_template,
                                      item.normalized_rule
                                        .expected_value_structure
                                    ) || (
                                      <span className="text-gray-500">
                                        No normalized rule statement template
                                        found.
                                      </span>
                                    )}
                                  </Box>
                                </FormField>

                                <FormField label="Source Text">
                                  <Box
                                    padding="m"
                                    className="rounded border border-gray-300 bg-gray-50 whitespace-pre-wrap"
                                  >
                                    {item.normalized_rule.source
                                      .source_text || (
                                      <span className="text-gray-500">
                                        No source text found.
                                      </span>
                                    )}
                                  </Box>
                                </FormField>
                                <FormField>
                                  <Box>
                                    {item.normalized_rule.source.contextIds
                                      .length > 0
                                      ? item.normalized_rule.source.contextIds.join(
                                          ', '
                                        )
                                      : 'No context IDs found.'}
                                  </Box>
                                </FormField>
                              </SpaceBetween>
                            </Container>
                          </div>
                        );
                      })}
                    </div>
                  </SpaceBetween>
                </section>
              ))}

              {manualBenchmarkDraft && shouldShowManualBenchmarkDraft ? (
                <section
                  key={MANUAL_BENCHMARK_CARD_KEY}
                  className="mx-auto w-full max-w-[52rem] min-w-0"
                >
                  <div className="grid w-full min-w-0 grid-cols-1 gap-4">
                    {(() => {
                      const isActive = showAllItemsActive
                        ? activeHighlightAllSelectionCardKey ===
                          MANUAL_BENCHMARK_CARD_KEY
                        : activeCandidateCardKey === MANUAL_BENCHMARK_CARD_KEY;
                      const toggleManualBenchmarkHighlight = async () => {
                        if (isActive) {
                          await highlightSingleItem(
                            MANUAL_BENCHMARK_CARD_KEY,
                            {
                              pageNumber: manualBenchmarkDraft.pageNumbers[0],
                              pageNumbers: manualBenchmarkDraft.pageNumbers,
                              query: manualBenchmarkDraft.sourceText,
                              reference: {
                                candidateCardKey: MANUAL_BENCHMARK_CARD_KEY,
                                pageNumbers: manualBenchmarkDraft.pageNumbers,
                                sourceText: manualBenchmarkDraft.sourceText
                              }
                            },
                            isActive
                          );
                          return;
                        }

                        if (!manualBenchmarkDraft.sourceText.trim()) {
                          exitHighlightAllMode();
                          await clearViewerAnnotations(false);
                          setActiveCandidateCardKey(MANUAL_BENCHMARK_CARD_KEY);
                          return;
                        }

                        await highlightSingleItem(
                          MANUAL_BENCHMARK_CARD_KEY,
                          {
                            pageNumber: manualBenchmarkDraft.pageNumbers[0],
                            pageNumbers: manualBenchmarkDraft.pageNumbers,
                            query: manualBenchmarkDraft.sourceText,
                            reference: {
                              candidateCardKey: MANUAL_BENCHMARK_CARD_KEY,
                              pageNumbers: manualBenchmarkDraft.pageNumbers,
                              sourceText: manualBenchmarkDraft.sourceText
                            }
                          },
                          isActive
                        );
                      };

                      return (
                        <div
                          key={MANUAL_BENCHMARK_CARD_KEY}
                          ref={(node) => {
                            candidateCardRefs.current[
                              MANUAL_BENCHMARK_CARD_KEY
                            ] = node;
                          }}
                          role="button"
                          tabIndex={0}
                          aria-pressed={isActive}
                          className={`w-full min-w-0 rounded-2xl transition focus:outline-none focus:ring-2 focus:ring-sky-400 ${
                            isActive ? 'ring-1 ring-inset ring-sky-400' : ''
                          }`}
                          onClick={toggleManualBenchmarkHighlight}
                          onKeyDown={(event) =>
                            handleInteractiveCardKeyDown(
                              event,
                              toggleManualBenchmarkHighlight
                            )
                          }
                        >
                          <Container
                            className={classNames(
                              'transition-shadow transition-colors',
                              isActive
                                ? 'border-sky-300 bg-sky-50/40 shadow-[0_0_0_1px_rgba(14,165,233,0.2),0_16px_36px_rgba(15,23,42,0.08)]'
                                : 'hover:border-slate-300 hover:bg-slate-50/60 hover:shadow-md'
                            )}
                            header={
                              <Header
                                variant="h3"
                                actions={
                                  <SpaceBetween
                                    direction="horizontal"
                                    size="xs"
                                  >
                                    <Button
                                      disabled={manualBenchmarkLoading}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleCancelManualBenchmarkDraft();
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      variant="primary"
                                      loading={manualBenchmarkLoading}
                                      disabled={
                                        !documentId ||
                                        manualBenchmarkLoading ||
                                        !manualBenchmarkDraft.sourceText.trim()
                                      }
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleCreateManualBenchmark();
                                      }}
                                    >
                                      Create benchmark rule
                                    </Button>
                                  </SpaceBetween>
                                }
                              >
                                New User Selected Benchmark
                              </Header>
                            }
                          >
                            <SpaceBetween size="l">
                              {manualBenchmarkError ? (
                                <Alert type="error">
                                  {manualBenchmarkError}
                                </Alert>
                              ) : null}

                              <FormField label="Normalized Rule">
                                <Box
                                  padding="m"
                                  className="rounded border border-gray-300 bg-gray-50 whitespace-pre-wrap"
                                >
                                  <span className="text-gray-500">
                                    Benchmark rule not created yet.
                                  </span>
                                </Box>
                              </FormField>

                              <FormField label="Source Text">
                                <div
                                  onClick={(event) => event.stopPropagation()}
                                  onKeyDown={(event) => event.stopPropagation()}
                                >
                                  <textarea
                                    value={manualBenchmarkDraft.sourceText}
                                    rows={8}
                                    placeholder="Edit the selected benchmark text before creating the rule."
                                    className="w-full rounded border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                                    onClick={(event) => event.stopPropagation()}
                                    onKeyDown={(event) =>
                                      event.stopPropagation()
                                    }
                                    onChange={(event) =>
                                      handleManualBenchmarkDraftSourceTextChange(
                                        event.target.value
                                      )
                                    }
                                  />
                                </div>
                              </FormField>

                              <FormField label="Pages">
                                <Box
                                  padding="m"
                                  className="rounded border border-gray-300 bg-gray-50 whitespace-pre-wrap"
                                >
                                  {manualBenchmarkDraft.pageNumbers.length > 0
                                    ? manualBenchmarkDraft.pageNumbers.join(
                                        ', '
                                      )
                                    : 'No page metadata found.'}
                                </Box>
                              </FormField>
                            </SpaceBetween>
                          </Container>
                        </div>
                      );
                    })()}
                  </div>
                </section>
              ) : null}
            </SpaceBetween>
          ) : null}
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={classNames(
        'mx-auto flex h-full min-h-0 flex-col px-2 py-2',
        reducedWidthMode
          ? 'w-full max-w-none'
          : 'w-[40vw] max-w-[40vw] min-w-[28rem]'
      )}
    >
      <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_18px_48px_rgba(15,23,42,0.08)]">
        <div className="shrink-0 border-b border-slate-200 bg-white px-4 py-4">
          {headerContent}
        </div>
        <div className="min-h-0 flex-1 bg-slate-50/40 px-4 py-3">
          {viewerContent}
        </div>
      </div>
    </div>
  );
};

export default PolicyDocumentViewer;
